import _ from 'lodash'
import React from 'react'
import {
    CampaignState,
    CampaignSummaryState,
    CampaignSummaryStep,
    CampaignSummaryTask,
    selectSummaryById,
} from './campaigns'
import {
    DiscussionState,
    SubmissionTaskState,
    SUBMISSION_VIEW_ENUM,
    VotesState,
    RejectSubmissionPayload,
    SubmissionProgressStatusCode,
    SubmissionState,
} from './submissions'
import { selectTaskByUuid, StepState, TaskState, selectTasksPlatformTypes } from './tasks'
import { createSelector } from 'reselect'
import i18n from 'config/i18n'
import {
    CProfileState,
    selectCanDirectReview,
    selectCanIndirectReview,
    selectIsInternalUser,
    selectUserPermissionCodes,
} from './profile'
import { MediaData } from 'components/MediaViewer'
import {
    GroupedOption,
    selectEditTagGroupOptions,
    selectIsEngagementStepTypeHelper,
    selectIsUrlOrScreenshotSubmissionStepTypeHelper,
    selectRejectionTagGroupOptions,
    TagOption,
    unSearchablePlatforms,
} from './constants'
import { BaseAction, selectCampaign, selectCampaignSummary, State } from 'state'
import { GridApi, GridFilterModel } from '@mui/x-data-grid-pro'
import { BLOCK_TYPE } from 'state/registrations'
import { COUNTRY_ENUM, PLATFORM_ENUM, STEP_TYPE_ENUM } from 'services/api/constants'
import { REGISTRATION_STATUS_ENUM } from 'services/api/registrations'
import { getDurationByDaysWithDates, getDurationByDays, dateIsInPast, parseBackendDate, addDay } from 'services/dates'
import { isSupportAddPostTimeStepType, engagementDataFields, getSortQueryText } from 'components/utils'
import { selectFollowerTierFilterOptions } from 'state/constants'
import { CampaignSubmission } from 'components/Shared/Permission/enums'
import { OptionsState } from 'particles/Dropdown'
import { REHYDRATE } from 'redux-persist'
import { SortItem } from 'components/Shared/MuiTable/PartiTable'
import { FIELDS_TYPE } from 'components/utils/filterAndSort'
import { selectRegistrationForm } from './registrations'
import { CAMPAIGN_STATUS_ENUM } from './campaigns'
import { Country } from 'services/api/constants'
import { TFunction } from 'i18next'

export const MAX_PAGE_SIZE = 40

export const SUBMISSION_VIEW_LABEL: any = {
    [SUBMISSION_VIEW_ENUM.FOR_REVIEW]: i18n.t('statusLabel.review', 'For Review'),
    [SUBMISSION_VIEW_ENUM.PENDING]: i18n.t('statusLabel.pending', 'Pending'),
    [SUBMISSION_VIEW_ENUM.COMPLETED]: i18n.t('statusLabel.completed', 'Completed'),
    [SUBMISSION_VIEW_ENUM.EDITS_REQUESTED]: i18n.t('statusLabel.editRequest', 'Edits Requested'),
}

export enum REGISTRATION_VIEW_ENUM {
    PENDING = 'registration_pending',
    WAITLISTED = 'waitlisted',
    AWAITING_RESPONSE = 'awaiting_response',
    ACCEPTED_INVITES = 'accepted_invites',
    JOINED_CAMPAIGN = 'joined_campaign',
    DECLINED_INVITES = 'declined_invites',
}

export enum OVERVIEW_ENUM {
    OVERVIEW = 'overview',
    REPORT = 'report',
}

export enum CAMPAIGN_REPORT_ENUM {
    OVERVIEW = 'overview',
    TASK_PERFORMANCE = 'taskPerformance',
    INDIVIDUAL_PERFORMANCE = 'individualPerformance',
    TOP_PERFORMERS = 'topPerformers',
}

export const DROPOUTS_AND_REJECTED = 'dropoutsAndRejected'

export type ViewType =
    | SUBMISSION_VIEW_ENUM
    | REGISTRATION_VIEW_ENUM
    | OVERVIEW_ENUM
    | CAMPAIGN_REPORT_ENUM
    | typeof DROPOUTS_AND_REJECTED

export type ReportType = CAMPAIGN_REPORT_ENUM

export const REGISTRATION_STATUS = {
    [REGISTRATION_VIEW_ENUM.PENDING]: REGISTRATION_STATUS_ENUM.PENDING,
    [REGISTRATION_VIEW_ENUM.WAITLISTED]: REGISTRATION_STATUS_ENUM.WAITLISTED,
    [REGISTRATION_VIEW_ENUM.AWAITING_RESPONSE]: REGISTRATION_STATUS_ENUM.AWAITING_RESPONSE,
    [REGISTRATION_VIEW_ENUM.ACCEPTED_INVITES]: REGISTRATION_STATUS_ENUM.ACCEPTED_INVITES,
    [REGISTRATION_VIEW_ENUM.JOINED_CAMPAIGN]: REGISTRATION_STATUS_ENUM.JOINED_CAMPAIGN,
    [REGISTRATION_VIEW_ENUM.DECLINED_INVITES]: REGISTRATION_STATUS_ENUM.DECLINED_INVITES,
}

export const REGISTRATION_VIEW_LABEL: any = {
    [REGISTRATION_VIEW_ENUM.PENDING]: i18n.t('statusLabel.pending', 'Pending'),
    [REGISTRATION_VIEW_ENUM.WAITLISTED]: i18n.t('statusLabel.waitlist', 'Waitlist'),
    [REGISTRATION_VIEW_ENUM.AWAITING_RESPONSE]: i18n.t('statusLabel.awaitingResponse', 'Awaiting Response'),
    [REGISTRATION_VIEW_ENUM.ACCEPTED_INVITES]: i18n.t('statusLabel.accepted', 'Accepted Invites'),
    [REGISTRATION_VIEW_ENUM.JOINED_CAMPAIGN]: i18n.t('statusLabel.joinedCampaign', 'Joined Campaign'),
    [REGISTRATION_VIEW_ENUM.DECLINED_INVITES]: i18n.t('statusLabel.declined', 'Declined Invites'),
}

export enum REVIEW_MODAL_TYPE {
    APPROVE = 'approve',
    EDIT_REQUEST = 'editRequest',
    REJECT = 'reject',
    APPROVE_PENDING = 'approvePending',
    REJECT_PENDING = 'rejectPending',
    BLOCK_BY_DETAIL_ACTION = 'blockByDetailAction',
    FILTER = 'filter',
    LEAVE = 'leave',
    BATCH_BUDGET_USED_UP = 'batchBudgetUsedUp',
    BATCH_ABOUT_WAITING_LIST = 'batchAboutWaitingList',
    BATCH_WHEN_WILL_SEE_WAITING_LIST = 'batchWhenWillSeeWaitingList',
    BATCH_SINGLE_REJECT = 'batchSingleReject',
    SINGLE_REJECT = 'singleReject',
    IN_WAITING_LIST = 'inWaitingList',
    REJECT_SUBMISSION_IN_WAITING_LIST = 'rejectSubmissionInWaitingList',
}

export enum VERIFY_TAB_TYPE {
    REVIEW = 'review',
    DISCUSSION = 'discussion',
}

export enum DECISION_EDITOR_ENUM {
    APPROVE = 'Approve',
    REQUEST_EDITS = 'Request edits',
    REJECT = 'Reject',
}
export enum MASS_REGISTRATION_ACTION_NAME_ENUM {
    APPROVE = 'approve',
    REJECT = 'reject',
    WAITLIST = 'waitlist',
    GIVE_PRIORITY = 'give_priority',
    REMOVE_PRIORITY = 'remove_priority',
    REVERT_USER_ACCEPTED_TO_PENDING = 'revert_user_accepted_to_pending',
    REVERT_WAITLISTED_TO_PENDING = 'revert_waitlisted_to_pending',
    REVERT_APPROVED_TO_PENDING = 'revert_approved_to_pending',
}

interface EditRequestHistoryModalState {
    isOpen: boolean
    submissionProgressUuid: string
    partiposterId?: string
}

interface ViewTypeCampaignMap<T> {
    viewType: Record<REGISTRATION_VIEW_ENUM | CAMPAIGN_REPORT_ENUM, Record<string, T>>
}

interface SubmissionChips {
    isVoteTypeChipOpen: boolean
    isPartiposterTagsChipOpen: boolean
    isResubmittedTypeChipOpen: boolean
}

interface DiscussionModalState {
    step: StepState
    submissionProgress: SubmissionProgressProps
    isOpen: boolean
}

interface OriginalCaptionModalState {
    isOpen: boolean
    submission: SubmissionState
}

interface CaptionDetailModalState {
    isOpen: boolean
    caption: string
    mediaData?: MediaData[]
    comment?: string
    timestamp?: string
    country?: string
}

interface RejectAllSubmissionModalState {
    isOpen: boolean
    selectedSubmissionUuids: string[]
}

export interface ReviewState {
    showSidebar: boolean
    selectedCampaignUuid: string | null
    sidebarTaskUuid: string | null
    sidebarStepUuid: string | null
    selectedSubmissionView: SUBMISSION_VIEW_ENUM | null
    selectedRegistrationView: REGISTRATION_VIEW_ENUM | null
    selectedOverview: OVERVIEW_ENUM | null
    selectedCampaignReport: CAMPAIGN_REPORT_ENUM | null
    searchTerm: string
    voteTypeFilter: OptionsState[]
    resubmittedTypeFilter: OptionsState[]
    partiposterTagFilter: TagOption[]
    submissionChips: SubmissionChips
    reviewPendingSubmission: string | null
    isMultiSelectMode: boolean
    massSubmissionUuids: string[]
    isModalOpen: boolean
    modalType: REVIEW_MODAL_TYPE
    modalFilterOptions: {
        voteTypeFilter: OptionsState[]
        partiposterTagFilter: TagOption[]
        resubmittedTypeFilter: OptionsState[]
    }
    modalPageNum: number
    activePageNum: number
    isMediaModalOpen: boolean
    mediaData: MediaData[]
    selectedMediaIndex: number
    selectedMediaContentUuids: { [submissionUuid: string]: string[] }
    selectedVerifyTab: { [submissionUuid: string]: VERIFY_TAB_TYPE }
    rejectPopupSubmissionUuid: string | null
    rejectPopupSubmissionProgressUuid: string | null
    rejectPopupTaskUuid: string | null
    showTaskStepInfoInVerify: boolean
    confirmRejectDataInWaitingList: RejectSubmissionPayload
    batchVerifyCardTopTaskUuid: { [submissionUuid: string]: string }
    selectedDecisionEditor: { [uuid: string]: DECISION_EDITOR_ENUM }
    selectedReviewData: { [uuid: string]: { [decisionEditor in DECISION_EDITOR_ENUM]: any } }
    selectedDiscussionData: { [uuid: string]: any }
    filterModel: ViewTypeCampaignMap<GridFilterModel | null>
    filterOptions: ViewTypeCampaignMap<Array<OptionsState>>
    sortItem: ViewTypeCampaignMap<SortItem | null>
    selectedPriorityListUuids: string[]
    selectedMuiTableRef: React.MutableRefObject<GridApi> | null
    selectedRowIds: string[]
    editRequestHistoryModal: EditRequestHistoryModalState
    parentChildLocationData: ParentChildLocationDataProps
    isCaptionReviewDone: boolean
    forceCaptionReviewSuccessProgressUuids: string[]
    rewriteCaptionReviewProgressUuids: string[]
    retryCaptionReviewProgressUuids: string[]
    infoToast: {
        isOpen: boolean
        message: string
    }
    discussionModal: DiscussionModalState
    rejectAllSubmissionModal: RejectAllSubmissionModalState
    originalCaptionModal: OriginalCaptionModalState
    captionDetailModal: CaptionDetailModalState
    singleTaskViews: SUBMISSION_VIEW_ENUM[]
    showReorderOverlay: boolean
}

const initialState: ReviewState = {
    showSidebar: window.innerWidth > 768, // default show sidebar on dekstop, but hide on mobile
    selectedCampaignUuid: null,
    sidebarTaskUuid: null,
    sidebarStepUuid: null,
    selectedSubmissionView: SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS,
    selectedRegistrationView: null,
    selectedOverview: null,
    selectedCampaignReport: CAMPAIGN_REPORT_ENUM.OVERVIEW,
    searchTerm: '',
    voteTypeFilter: [],
    resubmittedTypeFilter: [],
    partiposterTagFilter: [],
    submissionChips: {
        isVoteTypeChipOpen: false,
        isPartiposterTagsChipOpen: false,
        isResubmittedTypeChipOpen: false,
    },
    reviewPendingSubmission: null,
    isMultiSelectMode: false,
    massSubmissionUuids: [],
    isModalOpen: false,
    modalType: REVIEW_MODAL_TYPE.FILTER,
    modalFilterOptions: {
        voteTypeFilter: [],
        partiposterTagFilter: [],
        resubmittedTypeFilter: [],
    },
    modalPageNum: 0,
    activePageNum: 0,
    isMediaModalOpen: false,
    mediaData: [],
    selectedMediaIndex: 0,
    selectedMediaContentUuids: {},
    selectedVerifyTab: {},
    rejectPopupSubmissionUuid: null,
    rejectPopupSubmissionProgressUuid: null,
    rejectPopupTaskUuid: null,
    showTaskStepInfoInVerify: false,
    confirmRejectDataInWaitingList: {
        campaignUuid: '',
        submissionUuid: '',
        reason: '',
        getSubmissionListPayload: {
            submissionView: null,
        },
    },
    batchVerifyCardTopTaskUuid: {},
    selectedDecisionEditor: {},
    selectedReviewData: {},
    selectedDiscussionData: {},
    filterModel: {} as ViewTypeCampaignMap<GridFilterModel>,
    filterOptions: {} as ViewTypeCampaignMap<Array<OptionsState>>,
    sortItem: {} as ViewTypeCampaignMap<SortItem>,
    selectedPriorityListUuids: [],
    selectedMuiTableRef: null,
    selectedRowIds: [],
    editRequestHistoryModal: {} as EditRequestHistoryModalState,
    parentChildLocationData: {},
    isCaptionReviewDone: false,
    forceCaptionReviewSuccessProgressUuids: [],
    rewriteCaptionReviewProgressUuids: [],
    retryCaptionReviewProgressUuids: [],
    infoToast: {
        isOpen: false,
        message: '',
    },
    discussionModal: {} as DiscussionModalState,
    rejectAllSubmissionModal: {} as RejectAllSubmissionModalState,
    originalCaptionModal: {} as OriginalCaptionModalState,
    captionDetailModal: {} as CaptionDetailModalState,
    singleTaskViews: [],
    showReorderOverlay: false,
}

export enum ReviewActions {
    SET_SHOW_SIDEBAR = 'review/SET_SHOW_SIDEBAR',
    SET_SELECTED_CAMPAIGN_UUID = 'review/SET_SELECTED_CAMPAIGN_UUID',
    SET_SELECTED_TASK_UUID = 'review/SET_SELECTED_TASK_UUID',
    SET_SELECTED_STEP_UUID = 'review/SET_SELECTED_STEP_UUID',
    SET_REJECT_POPUP_SUBMISSION_UUID = 'review/SET_REJECT_POPUP_SUBMISSION_UUID',
    SET_REJECT_POPUP_SUBMISSION_PROGRESS_UUID = 'review/SET_REJECT_POPUP_SUBMISSION_PROGRESS_UUID',
    SET_SELECTED_SUBMISSION_VIEW = 'review/SET_SELECTED_SUBMISSION_VIEW',
    SET_SELECTED_REGISTRATION_VIEW = 'review/SET_SELECTED_REGISTRATION_VIEW',
    SET_SELECTED_OVERVIEW = 'review/SET_SELECTED_OVERVIEW',
    SET_SEARCH_TERM = 'review/SET_SEARCH_TERM',
    SET_VOTE_TYPE_FILTER = 'review/SET_VOTE_TYPE_FILTER',
    SET_PARTIPOSTER_TAG_FILTER = 'review/SET_PARTIPOSTER_TAG_FILTER',
    SET_SUBMISSION_CHIPS = 'review/SET_SUBMISSION_CHIPS',
    SET_REVIEW_PENDING_SUBMISSION = 'review/SET_REVIEW_PENDING_SUBMISSION',
    TOGGLE_MASS_SUBMISSION_UUIDS = 'review/TOGGLE_MASS_SUBMISSION_UUIDS',
    SET_MASS_SUBMISSION_UUIDS = 'review/SET_MASS_SUBMISSION_UUIDS',
    CLEAR_MASS_SUBMISSION_UUIDS = 'review/CLEAR_MASS_SUBMISSION_UUIDS',
    SET_MULTI_SELECT_MODE = 'review/SET_MULTI_SELECT_MODE',
    TOGGLE_MULTI_SELECT_MODE = 'review/TOGGLE_MULTI_SELECT_MODE',
    SET_MODAL_OPEN = 'review/SET_MODAL_OPEN',
    SET_MODAL_TYPE = 'review/SET_MODAL_TYPE',
    SET_MODAL_VOTE_TYPE_FILTER_OPTIONS = 'review/SET_MODAL_VOTE_TYPE_FILTER_OPTIONS',
    SET_MODAL_PARTIPOSTER_TAG_FILTER_OPTIONS = 'review/SET_MODAL_PARTIPOSTER_TAG_FILTER_OPTIONS',
    SET_MODAL_PAGE_NUM = 'review/SET_MODAL_PAGE_NUM',
    SET_ACTIVE_PAGE_NUM = 'review/SET_ACTIVE_PAGE_NUM',
    RESET_MULTI_SELECT_SETTING = 'review/RESET_MULTI_SELECT_SETTING',
    SET_MEDIA_MODAL_OPEN = 'review/SET_MEDIA_MODAL_OPEN',
    SET_MEDIA_DATA = 'review/SET_MEDIA_DATA',
    SET_SELECTED_MEDIA_INDEX = 'review/SET_SELECTED_MEDIA_INDEX',
    TOGGLE_SELECTED_MEDIA_CONTENT_UUIDS = 'review/TOGGLE_SELECTED_MEDIA_CONTENT_UUIDS',
    SET_SELECTED_MEDIA_CONTENT_UUIDS = 'review/SET_SELECTED_MEDIA_CONTENT_UUIDS',
    CLEAR_SELECTED_MEDIA_CONTENT_UUIDS = 'review/CLEAR_SELECTED_MEDIA_CONTENT_UUIDS',
    SET_SELECTED_VERIFY_TAB = 'review/SET_SELECTED_VERIFY_TAB',
    CLEAR_SELECTED_VERIFY_TAB = 'review/CLEAR_SELECTED_VERIFY_TAB',
    SET_SHOW_TASK_STEP_INFO_IN_VERIFY = 'review/SET_SHOW_TASK_STEP_INFO_IN_VERIFY',
    SET_CONFIRM_REJECT_DATA_IN_WAITING_LIST = 'review/SET_CONFIRM_REJECT_DATA_IN_WAITING_LIST',
    SET_BATCH_VERIFY_CARD_TOP_TASK_UUID = 'review/SET_BATCH_VERIFY_CARD_TOP_TASK_UUID',
    SET_SELECTED_DECISION_EDITOR = 'review/SET_SELECTED_DECISION_EDITOR',
    SET_SELECTED_REVIEW_DATA = 'review/SET_SELECTED_REVIEW_DATA',
    CLEAR_SELECTED_REVIEW_DATA = 'review/CLEAR_SELECTED_REVIEW_DATA',
    SET_SELECTED_DISCUSSION_DATA = 'review/SET_SELECTED_DISCUSSION_DATA',
    CLEAR_SELECTED_DISCUSSION_DATA = 'review/CLEAR_SELECTED_DISCUSSION_DATA',
    SET_FILTER_MODEL = 'review/SET_FILTER_MODEL',
    SET_FILTER_OPTIONS = 'review/SET_FILTER_OPTIONS',
    SET_SORT_ITEM = 'review/SET_SORT_ITEM',
    SET_PRIORITY_LIST_UUIDS = 'review/SET_PRIORITY_LIST_UUIDS',
    SET_MUI_TABLE_REF = 'review/SET_MUI_TABLE_REF',
    SET_SELECTED_ROW_IDS = 'review/SET_SELECTED_ROW_IDS',
    CLEAR_SELECTED_ROW_IDS = 'review/CLEAR_SELECTED_ROW_IDS',
    SET_SHOW_REORDER_OVERLAY = 'review/SET_SHOW_REORDER_OVERLAY',
    OPEN_EDIT_REQUEST_HISTORY_MODAL = 'review/OPEN_EDIT_REQUEST_HISTORY_MODAL',
    CLOSE_EDIT_REQUEST_HISTORY_MODAL = 'review/CLOSE_EDIT_REQUEST_HISTORY_MODAL',
    SET_PARENT_CHILD_LOCATION_DATA = 'review/SET_PARENT_CHILD_LOCATION_DATA',
    SET_RESUBMITTED_FILTER = 'review/SET_RESUBMITTED_FILTER',
    SET_MODAL_RESUBMITTED_FILTER_OPTIONS = 'review/SET_MODAL_RESUBMITTED_FILTER_OPTIONS',
    SET_SELECTED_CAMPAIGN_REPORT = 'review/SET_SELECTED_CAMPAIGN_REPORT',
    SET_IS_CAPTION_REVIEW_DONE = 'review/SET_IS_CAPTION_REVIEW_DONE',
    SET_FORCE_CAPTION_REVIEW_SUCCESS_PROGRESS_UUIDS = 'review/SET_FORCE_CAPTION_REVIEW_SUCCESS_PROGRESS_UUIDS',
    SET_REWRITE_CAPTION_REVIEW_PROGRESS_UUIDS = 'review/SET_REWRITE_CAPTION_REVIEW_PROGRESS_UUIDS',
    SET_RETRY_CAPTION_REVIEW_PROGRESS_UUIDS = 'review/SET_RETRY_CAPTION_REVIEW_PROGRESS_UUIDS',
    SET_INFO_TOAST_OPEN = 'review/SET_INFO_TOAST_OPEN',
    SET_INFO_TOAST_MESSAGE = 'review/SET_INFO_TOAST_MESSAGE',
    OPEN_DISCUSSION_MODAL = 'partiposter/OPEN_DISCUSSION_MODAL',
    CLOSE_DISCUSSION_MODAL = 'partiposter/CLOSE_DISCUSSION_MODAL',
    OPEN_REJECT_ALL_SUBMISSION_MODAL = 'partiposter/OPEN_REJECT_ALL_SUBMISSION_MODAL',
    CLOSE_REJECT_ALL_SUBMISSION_MODAL = 'partiposter/CLOSE_REJECT_ALL_SUBMISSION_MODAL',
    OPEN_ORIGINAL_CAPTION_MODAL = 'partiposter/OPEN_ORIGINAL_CAPTION_MODAL',
    CLOSE_ORIGINAL_CAPTION_MODAL = 'partiposter/CLOSE_ORIGINAL_CAPTION_MODAL',
    OPEN_CAPTION_DETAIL_MODAL = 'review/OPEN_CAPTION_DETAIL_MODAL',
    CLOSE_CAPTION_DETAIL_MODAL = 'review/CLOSE_CAPTION_DETAIL_MODAL',
    SET_SINGLE_TASK_VIEWS = 'review/SET_SINGLE_TASK_VIEWS',
}

interface SetSelectedData {
    uuid: string
    data: any
}
interface SetSelectedReviewData extends SetSelectedData {
    decisionEditor: DECISION_EDITOR_ENUM
}

export const clearSelectedReviewData = (payload: string) => ({
    payload,
    type: ReviewActions.CLEAR_SELECTED_REVIEW_DATA,
})

export const clearSelectedDiscussionData = (payload: string) => ({
    payload,
    type: ReviewActions.CLEAR_SELECTED_DISCUSSION_DATA,
})

export const setSelectedDiscussionData = (payload: SetSelectedData) => ({
    payload,
    type: ReviewActions.SET_SELECTED_DISCUSSION_DATA,
})

export const setSelectedReviewData = (payload: SetSelectedReviewData) => ({
    payload,
    type: ReviewActions.SET_SELECTED_REVIEW_DATA,
})

interface SetSelectedDecisionEditor {
    uuid: string
    type: DECISION_EDITOR_ENUM
}

export const setSelectedDecisionEditor = (payload: SetSelectedDecisionEditor) => ({
    payload,
    type: ReviewActions.SET_SELECTED_DECISION_EDITOR,
})

export const clearSelectedVerifyTab = () => ({
    type: ReviewActions.CLEAR_SELECTED_VERIFY_TAB,
})

interface SetSelectedVerifyTabProps {
    submissionUuid: string
    type: VERIFY_TAB_TYPE
}

export const setSelectedVerifyTab = (payload: SetSelectedVerifyTabProps) => ({
    payload,
    type: ReviewActions.SET_SELECTED_VERIFY_TAB,
})

interface ClearSelectedMediaContentProps {
    submissionUuid: string
}

interface ToggleSelectedMediaContentProps extends ClearSelectedMediaContentProps {
    contentUuid: string
}
interface SetSelectedMediaContentProps extends Omit<ToggleSelectedMediaContentProps, 'contentUuid'> {
    contentUuids: string[]
}

export const setSelectedMediaContent = (payload: SetSelectedMediaContentProps) => ({
    payload,
    type: ReviewActions.SET_SELECTED_MEDIA_CONTENT_UUIDS,
})

export const toggleSelectedMediaContent = (payload: ToggleSelectedMediaContentProps) => ({
    payload,
    type: ReviewActions.TOGGLE_SELECTED_MEDIA_CONTENT_UUIDS,
})

export const clearSelectedMediaContent = (payload: ClearSelectedMediaContentProps) => ({
    payload,
    type: ReviewActions.CLEAR_SELECTED_MEDIA_CONTENT_UUIDS,
})

export const setMediaModalOpen = (payload: boolean) => ({
    payload,
    type: ReviewActions.SET_MEDIA_MODAL_OPEN,
})

export const setMediaData = (payload: MediaData[]) => ({
    payload,
    type: ReviewActions.SET_MEDIA_DATA,
})

export const setSelectedMediaIndex = (payload: number) => ({
    payload,
    type: ReviewActions.SET_SELECTED_MEDIA_INDEX,
})

export const resetMultiSelectSetting = () => ({
    type: ReviewActions.RESET_MULTI_SELECT_SETTING,
})

export const setActivePageNum = (payload: number) => ({
    payload,
    type: ReviewActions.SET_ACTIVE_PAGE_NUM,
})

export const setReviewModalPageNum = (payload: number) => ({
    payload,
    type: ReviewActions.SET_MODAL_PAGE_NUM,
})

export const setReviewModalVoteTypeFilterOptions = (payload: OptionsState[]) => ({
    payload,
    type: ReviewActions.SET_MODAL_VOTE_TYPE_FILTER_OPTIONS,
})

export const setReviewModalResubmittedTypeFilterOptions = (payload: OptionsState[]) => ({
    payload,
    type: ReviewActions.SET_MODAL_RESUBMITTED_FILTER_OPTIONS,
})

export const setReviewModalPartiposterTagFilterOptions = (payload: TagOption[]) => ({
    payload,
    type: ReviewActions.SET_MODAL_PARTIPOSTER_TAG_FILTER_OPTIONS,
})

export const setReviewModalOpen = (payload: boolean) => ({
    payload,
    type: ReviewActions.SET_MODAL_OPEN,
})

export const setReviewModalType = (payload: REVIEW_MODAL_TYPE) => ({
    payload,
    type: ReviewActions.SET_MODAL_TYPE,
})

export const setMultiSelectMode = (payload: boolean) => ({
    payload,
    type: ReviewActions.SET_MULTI_SELECT_MODE,
})

export const toggleMultiSelectMode = () => ({
    type: ReviewActions.TOGGLE_MULTI_SELECT_MODE,
})

export const toggleMassSubmissionUuids = (payload: string[]) => ({
    payload,
    type: ReviewActions.TOGGLE_MASS_SUBMISSION_UUIDS,
})

export const setMassSubmissionUuids = (payload: string[]) => ({
    payload,
    type: ReviewActions.SET_MASS_SUBMISSION_UUIDS,
})

export const clearMassSubmissionUuids = () => ({
    type: ReviewActions.CLEAR_MASS_SUBMISSION_UUIDS,
})

export const setReviewPendingSubmission = (payload: string | null) => ({
    payload,
    type: ReviewActions.SET_REVIEW_PENDING_SUBMISSION,
})

export const setVoteTypeFilter = (payload: OptionsState[]) => ({
    payload,
    type: ReviewActions.SET_VOTE_TYPE_FILTER,
})

export const setResubmittedTypeFilter = (payload: OptionsState[]) => ({
    payload,
    type: ReviewActions.SET_RESUBMITTED_FILTER,
})

export const setPartiposterTagFilter = (payload: TagOption[]) => ({
    payload,
    type: ReviewActions.SET_PARTIPOSTER_TAG_FILTER,
})

export const setSubmissionChips = (payload: SubmissionChips) => ({
    payload,
    type: ReviewActions.SET_SUBMISSION_CHIPS,
})

export const setSearchTerm = (payload: string) => ({
    payload,
    type: ReviewActions.SET_SEARCH_TERM,
})

export const setSelectedSubmissionView = (payload: SUBMISSION_VIEW_ENUM | null) => ({
    payload,
    type: ReviewActions.SET_SELECTED_SUBMISSION_VIEW,
})

export const setSelectedRegistrationView = (payload: REGISTRATION_VIEW_ENUM | null) => ({
    payload,
    type: ReviewActions.SET_SELECTED_REGISTRATION_VIEW,
})

export const setSelectedOverview = (payload: OVERVIEW_ENUM | null) => ({
    payload,
    type: ReviewActions.SET_SELECTED_OVERVIEW,
})

export const setSelectedCampaignReport = (payload: CAMPAIGN_REPORT_ENUM | null) => ({
    payload,
    type: ReviewActions.SET_SELECTED_CAMPAIGN_REPORT,
})

export const setSelectedCampaignUuid = (payload: string | null) => ({
    payload,
    type: ReviewActions.SET_SELECTED_CAMPAIGN_UUID,
})

export const setSelectedTaskUuid = (payload: string | null) => ({
    payload,
    type: ReviewActions.SET_SELECTED_TASK_UUID,
})

export const setSelectedStepUuid = (payload: string | null) => ({
    payload,
    type: ReviewActions.SET_SELECTED_STEP_UUID,
})

export const setRejectPopupSubmissionUuid = (payload: string | null) => ({
    payload,
    type: ReviewActions.SET_REJECT_POPUP_SUBMISSION_UUID,
})

export const setRejectPopupSubmissionProgressUuid = (payload: string | null) => ({
    payload,
    type: ReviewActions.SET_REJECT_POPUP_SUBMISSION_PROGRESS_UUID,
})

export const setShowSidebar = (payload: boolean) => ({
    payload,
    type: ReviewActions.SET_SHOW_SIDEBAR,
})

export const setShowTaskStepInfoInVerify = (payload: boolean) => ({
    payload,
    type: ReviewActions.SET_SHOW_TASK_STEP_INFO_IN_VERIFY,
})

export const setConfirmRejectDataInWaitingList = (payload: RejectSubmissionPayload) => ({
    payload,
    type: ReviewActions.SET_CONFIRM_REJECT_DATA_IN_WAITING_LIST,
})

interface SetBatchVerifyCardTopTaskUuidProps {
    submissionUuid: string
    taskUuid: string
}

export const setBatchVerifyCardTopTaskUuid = (payload: SetBatchVerifyCardTopTaskUuidProps) => ({
    payload,
    type: ReviewActions.SET_BATCH_VERIFY_CARD_TOP_TASK_UUID,
})

interface SetViewTypeCampaignMapPayload<T> {
    campaignUuid: string
    viewType: REGISTRATION_VIEW_ENUM | CAMPAIGN_REPORT_ENUM
    options: T
}

export const setFilterModel = (payload: SetViewTypeCampaignMapPayload<GridFilterModel | null>) => ({
    payload,
    type: ReviewActions.SET_FILTER_MODEL,
})

export const setSortItem = (payload: SetViewTypeCampaignMapPayload<SortItem | null>) => ({
    payload,
    type: ReviewActions.SET_SORT_ITEM,
})

export const setFilterOptions = (payload: SetViewTypeCampaignMapPayload<Array<OptionsState>>) => ({
    payload,
    type: ReviewActions.SET_FILTER_OPTIONS,
})

interface ParentChildLocationDataProps {
    [parentId: string]: {
        label: string
        value: string
    }[]
}

export const setParentChildLocationData = (payload: ParentChildLocationDataProps) => ({
    payload,
    type: ReviewActions.SET_PARENT_CHILD_LOCATION_DATA,
})

export const setPriorityListUuids = (payload: string[]) => ({
    payload,
    type: ReviewActions.SET_PRIORITY_LIST_UUIDS,
})

export const setMuiTableRef = (payload: React.MutableRefObject<GridApi>) => ({
    payload,
    type: ReviewActions.SET_MUI_TABLE_REF,
})

export const setSelectedRowIds = (payload: string[]) => ({
    payload,
    type: ReviewActions.SET_SELECTED_ROW_IDS,
})
export const clearSelectedRowIds = () => ({
    type: ReviewActions.CLEAR_SELECTED_ROW_IDS,
})

export const setShowReorderOverlay = (payload: boolean) => ({
    type: ReviewActions.SET_SHOW_REORDER_OVERLAY,
    payload,
})

export const openEditRequestHistoryModal = (payload: EditRequestHistoryModalState) => ({
    payload,
    type: ReviewActions.OPEN_EDIT_REQUEST_HISTORY_MODAL,
})

export const closeEditRequestHistoryModal = () => ({
    type: ReviewActions.CLOSE_EDIT_REQUEST_HISTORY_MODAL,
})

export const setIsCaptionReviewDone = (payload: boolean) => ({
    payload,
    type: ReviewActions.SET_IS_CAPTION_REVIEW_DONE,
})

export const setForceCaptionReviewSuccessProgressUuids = (payload: string[]) => ({
    payload,
    type: ReviewActions.SET_FORCE_CAPTION_REVIEW_SUCCESS_PROGRESS_UUIDS,
})

export const setRewriteCaptionReviewProgressUuids = (payload: string[]) => ({
    payload,
    type: ReviewActions.SET_REWRITE_CAPTION_REVIEW_PROGRESS_UUIDS,
})

export const setRetryCaptionReviewProgressUuids = (payload: string[]) => ({
    payload,
    type: ReviewActions.SET_RETRY_CAPTION_REVIEW_PROGRESS_UUIDS,
})

export const setInfoToastOpen = (payload: boolean) => ({
    payload,
    type: ReviewActions.SET_INFO_TOAST_OPEN,
})

export const setInfoToastMessage = (payload: string) => ({
    payload,
    type: ReviewActions.SET_INFO_TOAST_MESSAGE,
})

export const openDiscussionModal = (payload: DiscussionModalState) => ({
    payload,
    type: ReviewActions.OPEN_DISCUSSION_MODAL,
})

export const closeDiscussionModal = () => ({
    type: ReviewActions.CLOSE_DISCUSSION_MODAL,
})

export const openRejectAllSubmissionModal = (payload: RejectAllSubmissionModalState) => ({
    payload,
    type: ReviewActions.OPEN_REJECT_ALL_SUBMISSION_MODAL,
})

export const closeRejectAllSubmissionModal = () => ({
    type: ReviewActions.CLOSE_REJECT_ALL_SUBMISSION_MODAL,
})

export const openOriginalCaptionModal = (payload: OriginalCaptionModalState) => ({
    payload,
    type: ReviewActions.OPEN_ORIGINAL_CAPTION_MODAL,
})

export const closeOriginalCaptionModal = () => ({
    type: ReviewActions.CLOSE_ORIGINAL_CAPTION_MODAL,
})

export const openCaptionDetailModal = (payload: CaptionDetailModalState) => ({
    payload,
    type: ReviewActions.OPEN_CAPTION_DETAIL_MODAL,
})

export const closeCaptionDetailModal = () => ({
    type: ReviewActions.CLOSE_CAPTION_DETAIL_MODAL,
})

export const setSingleTaskViews = (payload: SUBMISSION_VIEW_ENUM[]) => ({
    payload,
    type: ReviewActions.SET_SINGLE_TASK_VIEWS,
})

export const reviewReducer = (state: ReviewState = initialState, action: BaseAction) => {
    switch (action.type) {
        case ReviewActions.CLEAR_SELECTED_REVIEW_DATA: {
            return Object.assign({}, state, {
                ...state,
                selectedReviewData: { ...state.selectedReviewData, [action.payload]: undefined },
            })
        }
        case ReviewActions.CLEAR_SELECTED_DISCUSSION_DATA: {
            return Object.assign({}, state, {
                ...state,
                selectedDiscussionData: { ...state.selectedDiscussionData, [action.payload]: undefined },
            })
        }
        case ReviewActions.SET_SELECTED_DISCUSSION_DATA: {
            const { uuid, data }: SetSelectedData = action.payload
            return Object.assign({}, state, {
                ...state,
                selectedDiscussionData: {
                    ...state.selectedDiscussionData,
                    [uuid]: data,
                },
            })
        }
        case ReviewActions.SET_SELECTED_REVIEW_DATA: {
            const { uuid, data, decisionEditor }: SetSelectedReviewData = action.payload
            return Object.assign({}, state, {
                ...state,
                selectedReviewData: {
                    ...state.selectedReviewData,
                    [uuid]: {
                        ...state.selectedReviewData[uuid],
                        [decisionEditor]: data,
                    },
                },
            })
        }
        case ReviewActions.SET_SELECTED_DECISION_EDITOR: {
            const { uuid, type }: SetSelectedDecisionEditor = action.payload
            return Object.assign({}, state, {
                ...state,
                selectedDecisionEditor: {
                    ...state.selectedDecisionEditor,
                    [uuid]: type,
                },
            })
        }
        case ReviewActions.SET_SELECTED_VERIFY_TAB: {
            const { submissionUuid, type }: SetSelectedVerifyTabProps = action.payload
            return Object.assign({}, state, {
                ...state,
                selectedVerifyTab: {
                    ...state.selectedVerifyTab,
                    [submissionUuid]: type,
                },
            })
        }
        case ReviewActions.CLEAR_SELECTED_VERIFY_TAB: {
            return Object.assign({}, state, {
                ...state,
                selectedVerifyTab: initialState.selectedVerifyTab,
            })
        }
        case ReviewActions.SET_SELECTED_MEDIA_CONTENT_UUIDS: {
            const { submissionUuid, contentUuids }: SetSelectedMediaContentProps = action.payload
            return Object.assign({}, state, {
                ...state,
                selectedMediaContentUuids: {
                    ...state.selectedMediaContentUuids,
                    [submissionUuid]: contentUuids,
                },
            })
        }
        case ReviewActions.TOGGLE_SELECTED_MEDIA_CONTENT_UUIDS: {
            const { submissionUuid, contentUuid }: ToggleSelectedMediaContentProps = action.payload
            const selectedContentUuids = state.selectedMediaContentUuids[submissionUuid]
                ? state.selectedMediaContentUuids[submissionUuid]
                : []

            return Object.assign({}, state, {
                ...state,
                selectedMediaContentUuids: {
                    ...state.selectedMediaContentUuids,
                    [submissionUuid]: _.xor([...selectedContentUuids], [contentUuid]),
                },
            })
        }
        case ReviewActions.CLEAR_SELECTED_MEDIA_CONTENT_UUIDS: {
            const { submissionUuid }: ClearSelectedMediaContentProps = action.payload
            return Object.assign({}, state, {
                ...state,
                selectedMediaContentUuids: {
                    ...state.selectedMediaContentUuids,
                    [submissionUuid]: undefined,
                },
            })
        }
        case ReviewActions.SET_MEDIA_MODAL_OPEN: {
            return Object.assign({}, state, { ...state, isMediaModalOpen: action.payload })
        }
        case ReviewActions.SET_MEDIA_DATA: {
            return Object.assign({}, state, { ...state, mediaData: action.payload })
        }
        case ReviewActions.SET_SELECTED_MEDIA_INDEX: {
            return Object.assign({}, state, { ...state, selectedMediaIndex: action.payload })
        }
        case ReviewActions.RESET_MULTI_SELECT_SETTING: {
            return Object.assign({}, state, {
                ...state,
                massSubmissionUuids: initialState.massSubmissionUuids,
                isMultiSelectMode: initialState.isMultiSelectMode,
                activePageNum: initialState.activePageNum,
                modalFilterOptions: initialState.modalFilterOptions,
                modalPageNum: initialState.modalPageNum,
                modalType: initialState.modalType,
                reviewPendingSubmission: initialState.reviewPendingSubmission,
                selectedMediaContentUuids: initialState.selectedMediaContentUuids,
                selectedDecisionEditor: initialState.selectedDecisionEditor,
                selectedReviewData: initialState.selectedReviewData,
                selectedDiscussionData: initialState.selectedDiscussionData,
                selectedRowIds: initialState.selectedRowIds,
            })
        }
        case ReviewActions.SET_ACTIVE_PAGE_NUM: {
            return Object.assign({}, state, { ...state, activePageNum: action.payload })
        }
        case ReviewActions.SET_MODAL_PAGE_NUM: {
            return Object.assign({}, state, { ...state, modalPageNum: action.payload })
        }
        case ReviewActions.SET_MODAL_VOTE_TYPE_FILTER_OPTIONS: {
            return Object.assign({}, state, {
                ...state,
                modalFilterOptions: {
                    partiposterTagFilter: [],
                    voteTypeFilter: action.payload,
                    resubmittedTypeFilter: [],
                },
            })
        }
        case ReviewActions.SET_MODAL_RESUBMITTED_FILTER_OPTIONS: {
            return Object.assign({}, state, {
                ...state,
                modalFilterOptions: {
                    partiposterTagFilter: [],
                    voteTypeFilter: [],
                    resubmittedTypeFilter: action.payload,
                },
            })
        }
        case ReviewActions.SET_MODAL_PARTIPOSTER_TAG_FILTER_OPTIONS: {
            return Object.assign({}, state, {
                ...state,
                modalFilterOptions: {
                    voteTypeFilter: [],
                    partiposterTagFilter: action.payload,
                    resubmittedTypeFilter: [],
                },
            })
        }
        case ReviewActions.SET_MODAL_OPEN: {
            return Object.assign({}, state, { ...state, isModalOpen: action.payload })
        }
        case ReviewActions.SET_MODAL_TYPE: {
            return Object.assign({}, state, { ...state, modalType: action.payload })
        }
        case ReviewActions.SET_MULTI_SELECT_MODE: {
            return Object.assign({}, state, { ...state, isMultiSelectMode: action.payload })
        }
        case ReviewActions.TOGGLE_MULTI_SELECT_MODE: {
            return Object.assign({}, state, {
                ...state,
                isMultiSelectMode: !state.isMultiSelectMode,
                massSubmissionUuids: initialState.massSubmissionUuids,
            })
        }
        case ReviewActions.SET_MASS_SUBMISSION_UUIDS: {
            return Object.assign({}, state, {
                ...state,
                massSubmissionUuids: action.payload,
                isMultiSelectMode: !!action.payload?.length,
            })
        }
        case ReviewActions.TOGGLE_MASS_SUBMISSION_UUIDS: {
            const massSubmissionUuids = _.xor([...state.massSubmissionUuids], action.payload)
            return Object.assign({}, state, {
                ...state,
                massSubmissionUuids,
                isMultiSelectMode: !!massSubmissionUuids.length,
            })
        }
        case ReviewActions.CLEAR_MASS_SUBMISSION_UUIDS: {
            return Object.assign({}, state, {
                ...state,
                massSubmissionUuids: initialState.massSubmissionUuids,
                isMultiSelectMode: false,
            })
        }
        case ReviewActions.SET_REVIEW_PENDING_SUBMISSION: {
            return Object.assign({}, state, {
                ...state,
                reviewPendingSubmission: action.payload,
                selectedMediaContentUuids: initialState.selectedMediaContentUuids,
            })
        }
        case ReviewActions.SET_VOTE_TYPE_FILTER: {
            return Object.assign({}, state, { ...state, voteTypeFilter: action.payload })
        }
        case ReviewActions.SET_RESUBMITTED_FILTER: {
            return Object.assign({}, state, { ...state, resubmittedTypeFilter: action.payload })
        }
        case ReviewActions.SET_PARTIPOSTER_TAG_FILTER: {
            return Object.assign({}, state, { ...state, partiposterTagFilter: action.payload })
        }
        case ReviewActions.SET_SUBMISSION_CHIPS: {
            return Object.assign({}, state, { ...state, submissionChips: action.payload })
        }
        case ReviewActions.SET_SEARCH_TERM: {
            const prevSearchTerm = state.searchTerm
            const currSearchTerm = action.payload
            const prevActivePageNum = state.activePageNum
            return Object.assign({}, state, {
                ...state,
                activePageNum: prevSearchTerm && currSearchTerm ? prevActivePageNum : 0,
                searchTerm: currSearchTerm,
            })
        }
        case ReviewActions.SET_SELECTED_SUBMISSION_VIEW: {
            return Object.assign({}, state, {
                ...state,
                selectedRegistrationView: null,
                selectedSubmissionView: action.payload,
                searchTerm: '',
            })
        }
        case ReviewActions.SET_SELECTED_REGISTRATION_VIEW: {
            return Object.assign({}, state, {
                ...state,
                selectedRegistrationView: action.payload,
                selectedOverview: null,
                selectedSubmissionView: null,
                searchTerm: '',
            })
        }
        case ReviewActions.SET_SELECTED_OVERVIEW: {
            return Object.assign({}, state, {
                ...state,
                selectedOverview: action.payload,
                selectedSubmissionView: null,
                selectedRegistrationView: null,
                searchTerm: '',
            })
        }
        case ReviewActions.SET_SELECTED_CAMPAIGN_REPORT: {
            return Object.assign({}, state, {
                ...state,
                selectedCampaignReport: action.payload,
                selectedSubmissionView: null,
                selectedRegistrationView: null,
                searchTerm: '',
            })
        }
        case ReviewActions.SET_SELECTED_CAMPAIGN_UUID: {
            return Object.assign({}, state, { ...state, selectedCampaignUuid: action.payload })
        }
        case ReviewActions.SET_SELECTED_TASK_UUID: {
            return Object.assign({}, state, { ...state, sidebarTaskUuid: action.payload })
        }
        case ReviewActions.SET_SELECTED_STEP_UUID: {
            return Object.assign({}, state, { ...state, sidebarStepUuid: action.payload })
        }
        case ReviewActions.SET_REJECT_POPUP_SUBMISSION_UUID: {
            return Object.assign({}, state, { ...state, rejectPopupSubmissionUuid: action.payload })
        }
        case ReviewActions.SET_REJECT_POPUP_SUBMISSION_PROGRESS_UUID: {
            return Object.assign({}, state, { ...state, rejectPopupSubmissionProgressUuid: action.payload })
        }
        case ReviewActions.SET_SHOW_SIDEBAR: {
            return Object.assign({}, state, { ...state, showSidebar: action.payload })
        }
        case ReviewActions.SET_SHOW_TASK_STEP_INFO_IN_VERIFY: {
            return Object.assign({}, state, { ...state, showTaskStepInfoInVerify: action.payload })
        }
        case ReviewActions.SET_CONFIRM_REJECT_DATA_IN_WAITING_LIST: {
            return Object.assign({}, state, { ...state, confirmRejectDataInWaitingList: action.payload })
        }
        case ReviewActions.SET_BATCH_VERIFY_CARD_TOP_TASK_UUID: {
            const { submissionUuid, taskUuid }: SetBatchVerifyCardTopTaskUuidProps = action.payload
            return Object.assign({}, state, {
                ...state,
                batchVerifyCardTopTaskUuid: {
                    ...state.batchVerifyCardTopTaskUuid,
                    [submissionUuid]: taskUuid,
                },
            })
        }
        case ReviewActions.SET_FILTER_MODEL: {
            const payload: SetViewTypeCampaignMapPayload<GridFilterModel> = action.payload
            return Object.assign({}, state, {
                ...state,
                // note: active page num should be reset if the filtering rules changes
                activePageNum: 0,
                filterModel: {
                    viewType: {
                        ...state.filterModel.viewType,
                        [payload.viewType]: {
                            ...(state.filterModel?.viewType && state.filterModel.viewType[payload.viewType]
                                ? state.filterModel.viewType[payload.viewType]
                                : {}),
                            [payload.campaignUuid]: payload.options,
                        },
                    },
                },
            })
        }
        case ReviewActions.SET_SORT_ITEM: {
            const payload: SetViewTypeCampaignMapPayload<SortItem> = action.payload
            return Object.assign({}, state, {
                ...state,
                sortItem: {
                    viewType: {
                        ...state.sortItem.viewType,
                        [payload.viewType]: {
                            ...(state.sortItem?.viewType && state.sortItem.viewType[payload.viewType]
                                ? state.sortItem.viewType[payload.viewType]
                                : {}),
                            [payload.campaignUuid]: payload.options,
                        },
                    },
                },
            })
        }
        case ReviewActions.SET_FILTER_OPTIONS: {
            const payload: SetViewTypeCampaignMapPayload<Array<OptionsState>> = action.payload
            const result = Object.assign({}, state, {
                ...state,
                filterOptions: {
                    viewType: {
                        ...state.filterOptions.viewType,
                        [payload.viewType]: {
                            ...(state.filterOptions?.viewType && state.filterOptions.viewType[payload.viewType]
                                ? state.filterOptions.viewType[payload.viewType]
                                : {}),
                            [payload.campaignUuid]: payload.options,
                        },
                    },
                },
            })
            return result
        }
        case ReviewActions.SET_PARENT_CHILD_LOCATION_DATA: {
            return Object.assign({}, state, {
                ...state,
                parentChildLocationData: action.payload,
            })
        }
        case ReviewActions.SET_PRIORITY_LIST_UUIDS: {
            return Object.assign({}, state, { ...state, selectedPriorityListUuids: action.payload })
        }
        case ReviewActions.SET_MUI_TABLE_REF: {
            return Object.assign({}, state, { ...state, selectedMuiTableRef: action.payload })
        }
        case ReviewActions.SET_SELECTED_ROW_IDS: {
            return Object.assign({}, state, { ...state, selectedRowIds: action.payload })
        }
        case ReviewActions.CLEAR_SELECTED_ROW_IDS: {
            return Object.assign({}, state, { ...state, selectedRowIds: initialState.selectedRowIds })
        }
        case ReviewActions.SET_SHOW_REORDER_OVERLAY: {
            return Object.assign({}, state, { ...state, showReorderOverlay: action.payload })
        }
        case ReviewActions.OPEN_EDIT_REQUEST_HISTORY_MODAL: {
            return Object.assign({}, state, {
                editRequestHistoryModal: action.payload,
            })
        }
        case ReviewActions.CLOSE_EDIT_REQUEST_HISTORY_MODAL: {
            return Object.assign({}, state, {
                editRequestHistoryModal: initialState.editRequestHistoryModal,
            })
        }
        case ReviewActions.SET_IS_CAPTION_REVIEW_DONE: {
            return Object.assign({}, state, {
                isCaptionReviewDone: action.payload,
            })
        }
        case ReviewActions.SET_FORCE_CAPTION_REVIEW_SUCCESS_PROGRESS_UUIDS: {
            return Object.assign({}, state, {
                forceCaptionReviewSuccessProgressUuids: action.payload,
            })
        }
        case ReviewActions.SET_REWRITE_CAPTION_REVIEW_PROGRESS_UUIDS: {
            return Object.assign({}, state, {
                rewriteCaptionReviewProgressUuids: action.payload,
            })
        }
        case ReviewActions.SET_RETRY_CAPTION_REVIEW_PROGRESS_UUIDS: {
            return Object.assign({}, state, {
                retryCaptionReviewProgressUuids: action.payload,
            })
        }
        case ReviewActions.SET_INFO_TOAST_OPEN: {
            return Object.assign({}, state, {
                ...state,
                infoToast: {
                    ...state.infoToast,
                    isOpen: action.payload,
                },
            })
        }
        case ReviewActions.SET_INFO_TOAST_MESSAGE: {
            return Object.assign({}, state, {
                ...state,
                infoToast: {
                    ...state.infoToast,
                    message: action.payload,
                },
            })
        }
        case ReviewActions.OPEN_DISCUSSION_MODAL: {
            return Object.assign({}, state, {
                discussionModal: action.payload,
            })
        }
        case ReviewActions.CLOSE_DISCUSSION_MODAL: {
            return Object.assign({}, state, {
                discussionModal: initialState.discussionModal,
            })
        }
        case ReviewActions.OPEN_REJECT_ALL_SUBMISSION_MODAL: {
            return Object.assign({}, state, {
                rejectAllSubmissionModal: action.payload,
            })
        }
        case ReviewActions.CLOSE_REJECT_ALL_SUBMISSION_MODAL: {
            return Object.assign({}, state, {
                rejectAllSubmissionModal: initialState.rejectAllSubmissionModal,
            })
        }
        case ReviewActions.OPEN_ORIGINAL_CAPTION_MODAL: {
            return Object.assign({}, state, {
                originalCaptionModal: action.payload,
            })
        }
        case ReviewActions.CLOSE_ORIGINAL_CAPTION_MODAL: {
            return Object.assign({}, state, {
                originalCaptionModal: initialState.originalCaptionModal,
            })
        }
        case ReviewActions.OPEN_CAPTION_DETAIL_MODAL: {
            return Object.assign({}, state, {
                captionDetailModal: action.payload,
            })
        }
        case ReviewActions.CLOSE_CAPTION_DETAIL_MODAL: {
            return Object.assign({}, state, {
                captionDetailModal: initialState.captionDetailModal,
            })
        }
        case ReviewActions.SET_SINGLE_TASK_VIEWS: {
            return Object.assign({}, state, {
                singleTaskViews: action.payload,
            })
        }
        case REHYDRATE: {
            // Add review state initialState value to include it's default state
            return Object.assign({}, state, { ...state, ...initialState, ...action.payload?.review })
        }
        default:
            return state
    }
}

export interface CaptionReviewProps {
    final_verdict: 'Approve' | 'Request Edit'
    suggested_caption: string
    edit_reasons: string[]
}

export interface SubmissionProgressProps {
    uuid: string
    data?: any
    reviewed_data?: any
    pending_action?: any
    submission_task?: SubmissionTaskState
    comment: string
    tag_uuids?: string[]
    votes: VotesState
    latest_discussion: DiscussionState[]
    discussion_members: CProfileState[]
    submitted_at: string
    moved_out_of_pending_by?: CProfileState
    moved_out_of_pending_at?: string
    reviewed_by_system?: boolean
    partiposterId?: string
    resubmitted?: boolean
    caption_review_status?: 'FAILED' | 'SUCCESS'
    caption_review?: CaptionReviewProps
    status?: SubmissionProgressStatusCode
}

interface StepAndTaskProps {
    step?: StepState | null
    task?: TaskState | null
}
interface BlockProps {
    block?: any
}
interface SelectorSubmissionProgressProps {
    submissionProgress: SubmissionProgressProps
}

const selectPropsBlock = (_: any, props: BlockProps): any | undefined => props.block

const selectPropsStep = (_: any, props: StepAndTaskProps): StepState | undefined | null => props.step

const selectPropsTask = (_: any, props: StepAndTaskProps): TaskState | undefined | null => props.task

const selectPropsSubmissionProgress = (_: any, props: SelectorSubmissionProgressProps): SubmissionProgressProps =>
    props.submissionProgress

const selectReview = (state: State): ReviewState => state.review

export const selectShowSidebar = (state: State): boolean => selectReview(state).showSidebar
export const selectSelectedSubmissionView = (state: State): SUBMISSION_VIEW_ENUM | null =>
    selectReview(state).selectedSubmissionView
export const selectSelectedRegistrationView = (state: State): REGISTRATION_VIEW_ENUM | null =>
    selectReview(state).selectedRegistrationView
export const selectSelectedOverview = (state: State): OVERVIEW_ENUM | null => selectReview(state).selectedOverview
export const selectSelectedCampaignReport = (state: State): CAMPAIGN_REPORT_ENUM | null =>
    selectReview(state).selectedCampaignReport
export const selectSelectedCampaignUuid = (state: State): string | null => selectReview(state).selectedCampaignUuid
export const selectReviewingCampaignCountry = (state: State): COUNTRY_ENUM | null => {
    const uuid = selectSelectedCampaignUuid(state)
    if (uuid) {
        return selectCampaign(uuid)(state)?.country
    }
    return null
}
export const selectSelectedTaskUuid = (state: State): string | null => selectReview(state).sidebarTaskUuid
export const selectSidebarStepUuid = (state: State): string | null => selectReview(state).sidebarStepUuid
export const selectRejectPopupSubmissionUuid = (state: State): string | null =>
    selectReview(state).rejectPopupSubmissionUuid
export const selectRejectPopupSubmissionProgressUuid = (state: State): string | null =>
    selectReview(state).rejectPopupSubmissionProgressUuid
export const selectRejectPopupTaskUuid = (state: State): string | null => selectReview(state).rejectPopupTaskUuid
export const selectSearchTerm = (state: State): string => selectReview(state).searchTerm
export const selectVoteTypeFilter = (state: State): OptionsState[] => selectReview(state).voteTypeFilter
export const selectResubmittedTypeFilter = (state: State): OptionsState[] => selectReview(state).resubmittedTypeFilter
export const selectPartiposterTagFilter = (state: State): TagOption[] => selectReview(state).partiposterTagFilter
export const selectIsVoteTypeChipOpen = (state: State): boolean =>
    selectReview(state).submissionChips.isVoteTypeChipOpen
export const selectIsPartiposterTagsChipOpen = (state: State): boolean =>
    selectReview(state).submissionChips.isPartiposterTagsChipOpen
export const selectIsResubmittedTypeChipOpen = (state: State): boolean =>
    selectReview(state).submissionChips.isResubmittedTypeChipOpen
export const selectReviewPendingSubmission = (state: State): string | null =>
    selectReview(state).reviewPendingSubmission
export const selectMassSubmissionUuids = (state: State): string[] => selectReview(state).massSubmissionUuids
export const selectIsMultiSelectMode = (state: State): boolean => selectReview(state).isMultiSelectMode
export const selectReviewIsModalOpen = (state: State): boolean => selectReview(state).isModalOpen
export const selectReviewModalType = (state: State): REVIEW_MODAL_TYPE => selectReview(state).modalType
export const selectReviewModalFilterOptions = (state: State) => selectReview(state).modalFilterOptions
export const selectReviewModalPageNum = (state: State): number => selectReview(state).modalPageNum
export const selectActivePageNum = (state: State): number => selectReview(state).activePageNum
export const selectIsCaptionReviewDone = (state: State): boolean => selectReview(state).isCaptionReviewDone
export const selectForceCaptionReviewSuccessProgressUuids = (state: State): string[] =>
    selectReview(state).forceCaptionReviewSuccessProgressUuids
export const selectRewriteCaptionReviewProgressUuids = (state: State): string[] =>
    selectReview(state).rewriteCaptionReviewProgressUuids
export const selectRetryCaptionReviewProgressUuids = (state: State): string[] =>
    selectReview(state).retryCaptionReviewProgressUuids
export const selectInfoToastOpen = (state: State): boolean => selectReview(state).infoToast.isOpen
export const selectInfoToastMessage = (state: State): string => selectReview(state).infoToast.message

export const selectSelectedMediaContentUuids =
    (submissionUuid: string) =>
    (state: State): string[] => {
        const result = selectReview(state).selectedMediaContentUuids[submissionUuid]
        return result ? result : []
    }
export const selectSelectedVerifyTab =
    (submissionUuid: string) =>
    (state: State): VERIFY_TAB_TYPE => {
        const result = selectReview(state).selectedVerifyTab[submissionUuid]
        return result ? result : VERIFY_TAB_TYPE.REVIEW
    }

interface SelectPropsMediaContentSelectorProps extends SelectorSubmissionProgressProps {
    defaultFirstContent?: boolean
}

const selectPropsMediaContentSelector = (
    _: any,
    props: SelectPropsMediaContentSelectorProps,
): SubmissionProgressProps => props.submissionProgress

export const makeSelectedMediaContentsSelector = () =>
    createSelector(
        [
            selectPropsMediaContentSelector,
            (state, props): string[] => {
                const { submissionProgress } = props
                if (submissionProgress) {
                    if (
                        props.defaultFirstContent &&
                        submissionProgress.data &&
                        submissionProgress.data.files &&
                        submissionProgress.data.files.length === 1
                    ) {
                        return [submissionProgress.data.files[0].uuid]
                    } else {
                        return selectSelectedMediaContentUuids(props.submissionProgress.uuid)(state)
                    }
                } else {
                    return []
                }
            },
        ],
        (submissionProgress, contentUuids) => {
            if (
                submissionProgress &&
                submissionProgress.data &&
                submissionProgress.data.files &&
                submissionProgress.data.files.length > 0 &&
                contentUuids.length > 0
            ) {
                return contentUuids.map(uuid => submissionProgress.data.files.find((file: any) => file.uuid === uuid))
            } else {
                return []
            }
        },
    )

export const selectIsMediaModalOpen = (state: State): boolean => selectReview(state).isMediaModalOpen
export const selectMediaData = (state: State): MediaData[] => selectReview(state).mediaData
export const selectSelectedMediaIndex = (state: State): number => selectReview(state).selectedMediaIndex

export const selectIsMassSubmissionUuidExists =
    (submissionUuid: string) =>
    (state: State): boolean => {
        const uuids = selectMassSubmissionUuids(state)
        return uuids.filter(uuid => uuid === submissionUuid).length > 0
    }

export const selectSelectedCampaign = (state: State): CampaignState | null => {
    const uuid = selectSelectedCampaignUuid(state)
    if (uuid) {
        return selectCampaign(uuid)(state)
    }
    return null
}

export const selectSelectedCampaignSummary = (state: State): CampaignSummaryState | null => {
    const uuid = selectSelectedCampaignUuid(state)
    if (uuid) {
        return selectCampaignSummary(uuid)(state)
    }
    return null
}

export const selectIsSelectedCampaignDeadlineOver = createSelector(
    [selectSelectedCampaign],
    campaign => campaign && dateIsInPast(campaign.time_window.end, campaign.country),
)

export const selectSidebarTask = (state: State): TaskState | undefined | null => {
    const uuid = selectSelectedTaskUuid(state)
    if (uuid) {
        return selectTaskByUuid(uuid)(state)
    }
    return null
}

export const makeNextStepSelector = () =>
    createSelector([selectPropsTask, selectPropsStep], (task, step) => {
        if (task && step) {
            const index = task.steps.findIndex((taskStep: StepState) => taskStep.uuid === step.uuid)
            return index < task.steps.length - 1 ? task.steps[index + 1] : undefined
        }
        return undefined
    })

export const selectSidebarStep = createSelector(
    [selectSidebarTask, selectSidebarStepUuid],
    (sidebarTask, sidebarStepUuid) =>
        sidebarTask && sidebarTask.steps.find((step: StepState) => step.uuid === sidebarStepUuid),
)

export const selectSelectedStepSummary = createSelector(
    [selectSelectedCampaignUuid, selectSelectedTaskUuid, selectSidebarStepUuid, selectSummaryById],
    (selectedCampaignUuid, sidebarTaskUuid, sidebarStepUuid, campaignSummaryById) => {
        if (!selectedCampaignUuid || !sidebarTaskUuid || !sidebarStepUuid) {
            return null
        }
        const campaignSummary = campaignSummaryById[selectedCampaignUuid]
        const selectedTaskSummary =
            campaignSummary && campaignSummary.tasks.find((task: CampaignSummaryTask) => task.uuid === sidebarTaskUuid)
        if (!selectedTaskSummary) {
            return null
        }
        return selectedTaskSummary.steps.find((step: CampaignSummaryStep) => step.uuid === sidebarStepUuid)
    },
)

export const selectNavBarTitle = createSelector(
    [
        selectSelectedCampaign,
        selectSidebarTask,
        selectSidebarStep,
        selectSelectedSubmissionView,
        selectSelectedRegistrationView,
    ],
    (campaign, task, step, submissionView, registrationView) => {
        if (registrationView)
            return `${campaign && campaign.title} > ${i18n.t(
                'submission.review.registrationTitle',
                'REGISTRATION',
            )} > ${REGISTRATION_VIEW_LABEL[registrationView]}`
        return submissionView === SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS
            ? `${campaign && campaign.title} > ${i18n.t('submission.review.allSubmissionsTitle', 'All submissions')}`
            : submissionView === null
              ? `${campaign && campaign.title} > ${i18n.t('submission.review.campaignOverviewTitle')}`
              : `${campaign && campaign.title} > ${task && task.title} > ${step && step.data && step.data.title} > ${
                    SUBMISSION_VIEW_LABEL[submissionView]
                }`
    },
)

export const selectIsSupportMassApprovalTableStepHelper = createSelector(
    (state: State) => selectIsEngagementStepTypeHelper(state),
    (state: State) => selectIsUrlOrScreenshotSubmissionStepTypeHelper(state),
    (isEngagementStepType, isUrlOrScreenshotSubmissionStepType) => {
        return (stepType: STEP_TYPE_ENUM, schedulePostEnabled = false): boolean => {
            // this will return support mass approval on MUI Review Table on these condition:
            // 1. the step type is URL/Screenshot submission step
            // 2. the step type is engagement step
            // 3. step 1 of supported Schedule Post task, when “Schedule Post” (task.data.add_post_time) is enabled for the task

            return (
                isUrlOrScreenshotSubmissionStepType(stepType) ||
                isEngagementStepType(stepType) ||
                (schedulePostEnabled && isSupportAddPostTimeStepType(stepType))
            )
        }
    },
)

export const selectShowMultiSelectToggle = createSelector(
    [
        selectSelectedRegistrationView,
        selectSelectedSubmissionView,
        selectSidebarStep,
        selectIsSupportMassApprovalTableStepHelper,
        selectCanDirectReview,
        selectCanIndirectReview,
        (state: State) => selectCheckCanReviewSubmissionByStep(state),
    ],
    (
        registrationView,
        submissionView,
        selectedStep,
        isSupportMassApprovalTableStep,
        canDirectReview,
        canIndirectReview,
        checkCanReviewSubmissionByStep,
    ) => {
        if (!selectedStep || registrationView !== null) {
            return false
        }

        if (isSupportMassApprovalTableStep(selectedStep.type)) {
            return submissionView === SUBMISSION_VIEW_ENUM.PENDING && checkCanReviewSubmissionByStep(selectedStep.type)
        }

        return (
            ((submissionView === SUBMISSION_VIEW_ENUM.PENDING && (canDirectReview || canIndirectReview)) ||
                (submissionView === SUBMISSION_VIEW_ENUM.FOR_REVIEW && canDirectReview)) &&
            !selectedStep.type.includes('url') && // exclude ig/fb post url step
            ((selectedStep.data.blocks &&
                selectedStep.data.blocks.find((obj: any) => {
                    return obj.type == 'photo_submission'
                }) != undefined) ||
                selectedStep.type.includes('submit'))
        ) //exclude custom task text || url submission and other none submit step
    },
)

export const selectIsMassModalBlocked = createSelector(
    [selectSidebarTask, selectSidebarStep, selectIsSupportMassApprovalTableStepHelper],
    (sidebarTask, selectedStep, isSupportMassApprovalTableStep) => {
        if (
            selectedStep &&
            isSupportMassApprovalTableStep(
                selectedStep.type,
                sidebarTask && sidebarTask.data ? sidebarTask.data.add_post_time : undefined,
            )
        ) {
            return false
        }

        return (
            sidebarTask &&
            sidebarTask.data &&
            (sidebarTask.data.add_post_time || sidebarTask.data.link_in_bio_type == 2)
        )
    },
)

const buildParam = (prev: string, element: OptionsState | TagOption) => {
    if (prev.length == 0) {
        return element.value
    } else {
        return prev + ',' + element.value
    }
}

export const selectResubmittedParam = createSelector(
    [selectResubmittedTypeFilter],
    reSubmittedOptions => reSubmittedOptions && reSubmittedOptions.reduce(buildParam, ''),
)

export const selectVoteTypesParam = createSelector(
    [selectVoteTypeFilter],
    voteTypeOptions => voteTypeOptions && voteTypeOptions.reduce(buildParam, ''),
)

export const selectPartiposterTagsParam = createSelector(
    [selectPartiposterTagFilter],
    partiposterTagFilter => partiposterTagFilter && partiposterTagFilter.reduce(buildParam, ''),
)

export const selectSubmissionListParams = createSelector(
    [
        selectPartiposterTagsParam,
        selectVoteTypesParam,
        selectResubmittedParam,
        selectSearchTerm,
        selectSelectedSubmissionView,
    ],
    (partiposterTagParam, voteTypesParam, resubmittedParam, searchTerm, selectedSubmissionView) => ({
        submissionView: selectedSubmissionView,
        keywords: searchTerm,
        vote_type: voteTypesParam,
        tags: partiposterTagParam,
        resubmitted: resubmittedParam,
    }),
)

export const selectShowOriginalSubmission = createSelector(
    [selectSelectedSubmissionView, selectPropsSubmissionProgress],
    (submissionView, submissionProgress) =>
        submissionView === SUBMISSION_VIEW_ENUM.PENDING ||
        submissionView === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED ||
        (submissionView === SUBMISSION_VIEW_ENUM.FOR_REVIEW &&
            submissionProgress.pending_action !== null &&
            submissionProgress.pending_action.action_type === '2') ||
        (submissionView === SUBMISSION_VIEW_ENUM.FOR_REVIEW &&
            submissionProgress.pending_action !== null &&
            submissionProgress.pending_action.action_type === '3'),
)

export const makeSubmissionContentSelector = () =>
    createSelector(
        [
            selectPropsStep,
            selectSelectedSubmissionView,
            selectPropsSubmissionProgress,
            selectShowOriginalSubmission,
            selectReviewPendingSubmission,
        ],
        (step, submissionView, submissionProgress, showOriginalSubmission, isVerifyMode) => {
            let submissionContent, post_title, captionData, url, video_code, engagementData
            if (step && step.type !== STEP_TYPE_ENUM.CUSTOM) {
                submissionContent =
                    submissionView === SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS || showOriginalSubmission
                        ? submissionProgress.data.files
                        : submissionProgress.reviewed_data.files
                url = submissionView === SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS ? submissionProgress.data.url : null
                video_code =
                    submissionView === SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS
                        ? submissionProgress.data.video_code
                        : null
                if (
                    isVerifyMode ||
                    submissionView === SUBMISSION_VIEW_ENUM.FOR_REVIEW ||
                    submissionView === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED ||
                    submissionView === SUBMISSION_VIEW_ENUM.COMPLETED ||
                    submissionView === SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS
                ) {
                    post_title =
                        showOriginalSubmission || submissionView === SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS
                            ? submissionProgress.data.post_title
                            : submissionProgress.reviewed_data.post_title
                    captionData =
                        showOriginalSubmission || submissionView === SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS
                            ? submissionProgress.data.caption
                            : submissionProgress.reviewed_data.caption
                }
                if (
                    submissionView === SUBMISSION_VIEW_ENUM.FOR_REVIEW &&
                    submissionProgress.pending_action !== null &&
                    submissionProgress.pending_action.action_type === '1'
                ) {
                    submissionContent = submissionProgress.pending_action.data.data.files
                    post_title = submissionProgress.pending_action.data.data.post_title
                    captionData = submissionProgress.pending_action.data.data.caption
                }

                engagementData = null
                if (submissionView === SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS) {
                    engagementData = _.pick(submissionProgress.data, engagementDataFields)
                }
            }

            return {
                submissionContent,
                post_title,
                captionData,
                url,
                video_code,
                engagementData,
            }
        },
    )

export const makeCustomSubmissionContentSelector = () =>
    createSelector(
        [selectPropsBlock, selectSelectedSubmissionView, selectPropsSubmissionProgress, selectShowOriginalSubmission],
        (block, submissionView, submissionProgress, showOriginalSubmission) => {
            if (!block) {
                return null
            }
            let result =
                showOriginalSubmission || submissionView === SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS
                    ? submissionProgress.data[block.uuid]
                    : submissionProgress.reviewed_data[block.uuid]
            if (
                submissionView === SUBMISSION_VIEW_ENUM.FOR_REVIEW &&
                submissionProgress.pending_action !== null &&
                submissionProgress.pending_action.action_type === '1'
            ) {
                result = submissionProgress.pending_action.data.data[block.uuid]
            }

            return result
        },
    )

export const makeShowLinkInBioSelector = () =>
    createSelector(
        [selectSelectedSubmissionView, selectPropsStep, selectPropsTask],
        (selectedSubmissionView, step, task): boolean => {
            if (
                step &&
                ![
                    STEP_TYPE_ENUM.SUBMIT_CONTENT_IG_POST,
                    STEP_TYPE_ENUM.SUBMIT_CONTENT_IG_STORY,
                    STEP_TYPE_ENUM.SUBMIT_CONTENT_TIKTOK_POST,
                    STEP_TYPE_ENUM.SUBMIT_CONTENT_IG_REEL,
                ].includes(step.type)
            ) {
                return false
            }

            return (
                task &&
                task.data &&
                (task.data.link_in_bio_type === 1 ||
                    (task.data.link_in_bio_type === 2 &&
                        selectedSubmissionView !== SUBMISSION_VIEW_ENUM.PENDING &&
                        selectedSubmissionView !== SUBMISSION_VIEW_ENUM.EDITS_REQUESTED))
            )
        },
    )

export const makeShowCommentSelector = () =>
    createSelector([selectPropsStep, selectSelectedSubmissionView], (step, selectedSubmissioView) => {
        if (
            step &&
            [
                STEP_TYPE_ENUM.SUBMIT_CONTENT_FB_POST,
                STEP_TYPE_ENUM.SUBMIT_CONTENT_IG_POST,
                STEP_TYPE_ENUM.SUBMIT_CONTENT_IG_STORY,
                STEP_TYPE_ENUM.SUBMIT_CONTENT_TIKTOK_POST,
                STEP_TYPE_ENUM.SUBMIT_CONTENT_IG_REEL,
                STEP_TYPE_ENUM.SUBMIT_CONTENT_XIAOHONGSHU_POST,
                STEP_TYPE_ENUM.CUSTOM,
                STEP_TYPE_ENUM.SUBMIT_PROOF_PHOTO,
            ].includes(step.type)
        ) {
            switch (selectedSubmissioView) {
                case SUBMISSION_VIEW_ENUM.COMPLETED:
                case SUBMISSION_VIEW_ENUM.FOR_REVIEW:
                    return true
                default:
                    return false
            }
        }
        return false
    })

export const makeCommentTagBadgesSelector = () =>
    createSelector(
        [
            selectPropsSubmissionProgress,
            selectSelectedSubmissionView,
            selectEditTagGroupOptions(),
            selectRejectionTagGroupOptions(),
        ],
        (submissionProgress, selectedSubmissionView, editTagGroupOptions, rejectionTagGroupOptions) => {
            if (submissionProgress === null) {
                return []
            }
            if (selectedSubmissionView === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED) {
                const { tag_uuids: tagUuids } = submissionProgress
                return tagUuids
                    ? editTagGroupOptions
                          .map((groupOption: GroupedOption) => {
                              return groupOption.options.filter((tag: TagOption) =>
                                  tagUuids && tag ? tagUuids.includes(tag.uuid) : false,
                              )
                          })
                          .flat()
                    : []
            } else if (selectedSubmissionView === SUBMISSION_VIEW_ENUM.FOR_REVIEW) {
                const { pending_action: pendingAction } = submissionProgress
                if (pendingAction === null) {
                    return []
                }
                const { action_type: actionType, data } = pendingAction
                return actionType && data && data.tag_uuids
                    ? actionType === '3'
                        ? editTagGroupOptions
                              .map((groupOption: GroupedOption) => {
                                  return groupOption.options.filter((tag: TagOption) =>
                                      data.tag_uuids.includes(tag.uuid),
                                  )
                              })
                              .flat()
                        : actionType === '2'
                          ? rejectionTagGroupOptions
                                .map((groupOption: GroupedOption) => {
                                    return groupOption.options.filter((tag: TagOption) =>
                                        data.tag_uuids.includes(tag.uuid),
                                    )
                                })
                                .flat()
                          : []
                    : []
            }
            return []
        },
    )

export const selectCheckCanReviewSubmissionByStep = createSelector(
    state => selectIsEngagementStepTypeHelper(state),
    state => selectIsUrlOrScreenshotSubmissionStepTypeHelper(state),
    selectUserPermissionCodes,
    selectSelectedSubmissionView,
    (isEngagementStepType, isUrlOrScreenshotSubmissionStepType, permissionsSet, submissionView) => {
        return (stepType: STEP_TYPE_ENUM) => {
            let canReview =
                permissionsSet.has(CampaignSubmission.ReviewDirectly) ||
                permissionsSet.has(CampaignSubmission.ReviewIndirectly)

            // indirect review should not have permission in for_review (accept & send back to pending)
            if (submissionView === SUBMISSION_VIEW_ENUM.FOR_REVIEW) {
                canReview = permissionsSet.has(CampaignSubmission.ReviewDirectly)
            }
            return isUrlOrScreenshotSubmissionStepType(stepType)
                ? permissionsSet.has(CampaignSubmission.ReviewUrlOrScreenshotStep)
                : isEngagementStepType(stepType)
                  ? permissionsSet.has(CampaignSubmission.ReviewEngagementStep)
                  : canReview
        }
    },
)

export const selectShowReviewButtons = createSelector(
    [selectSelectedSubmissionView, selectCanDirectReview],
    (selectedSubmissionView, canDirectReview) =>
        selectedSubmissionView === SUBMISSION_VIEW_ENUM.FOR_REVIEW && canDirectReview,
)

export const makeShowPostTimeSelector = () =>
    createSelector(
        [selectPropsSubmissionProgress, selectPropsTask, selectPropsStep, selectSelectedSubmissionView],
        (submissionProgress, task, step, selectedSubmissionView) => {
            if (step && !isSupportAddPostTimeStepType(step.type)) {
                return false
            }
            if (selectedSubmissionView === SUBMISSION_VIEW_ENUM.FOR_REVIEW) {
                return (
                    task &&
                    task.data &&
                    task.data.add_post_time &&
                    submissionProgress &&
                    submissionProgress.pending_action &&
                    submissionProgress.pending_action.data &&
                    submissionProgress.pending_action.data.data &&
                    submissionProgress.pending_action.data.data['post_time_window'] != null &&
                    submissionProgress.pending_action.data.data['post_time_window'].start != null &&
                    submissionProgress.pending_action.data.data['post_time_window'].end != null &&
                    submissionProgress.pending_action.data.data['post_time_window'].start.length > 0 &&
                    submissionProgress.pending_action.data.data['post_time_window'].end.length > 0
                )
            } else if (selectedSubmissionView === SUBMISSION_VIEW_ENUM.COMPLETED) {
                return task && task.data && task.data.add_post_time
            }
            return false
        },
    )

export const makeShowCommentAndVoteSelector = () =>
    createSelector([selectPropsStep], step => {
        if (step) {
            return [
                STEP_TYPE_ENUM.SUBMIT_CONTENT_FB_POST,
                STEP_TYPE_ENUM.SUBMIT_CONTENT_IG_POST,
                STEP_TYPE_ENUM.SUBMIT_CONTENT_IG_STORY,
                STEP_TYPE_ENUM.SUBMIT_CONTENT_TIKTOK_POST,
                STEP_TYPE_ENUM.SUBMIT_CONTENT_IG_REEL,
                STEP_TYPE_ENUM.SUBMIT_CONTENT_XIAOHONGSHU_POST,
                STEP_TYPE_ENUM.CUSTOM,
            ].includes(step.type)
        }

        return false
    })

export const selectShowTaskStepInfoInVerify = (state: State): boolean => selectReview(state).showTaskStepInfoInVerify
export const selectConfirmRejectDataInWaitingList = (state: State): RejectSubmissionPayload =>
    selectReview(state).confirmRejectDataInWaitingList

export const selectBatchVerifyCardTopTaskUuid =
    (submissionUuid: string) =>
    (state: State): string => {
        const result = selectReview(state).batchVerifyCardTopTaskUuid[submissionUuid]
        return result ? result : ''
    }

export const selectSelectedDecisionEditor =
    (uuid: string) =>
    (state: State): DECISION_EDITOR_ENUM => {
        const result = selectReview(state).selectedDecisionEditor[uuid]
        return result ? result : DECISION_EDITOR_ENUM.APPROVE
    }

export const selectSelectedReviewData = (uuid: string, decisionEditor: DECISION_EDITOR_ENUM) => (state: State) => {
    const reviewData = selectReview(state).selectedReviewData[uuid]
    if (reviewData) {
        return reviewData[decisionEditor]
    }
    return
}

export const selectSelectedDiscussionData = (uuid: string) => (state: State) =>
    selectReview(state).selectedDiscussionData[uuid]

const getViewTypeCampaignOptions = <T>(
    state: ViewTypeCampaignMap<T | null>,
    viewType?: REGISTRATION_VIEW_ENUM | null,
    campaignUuid?: string | null,
): T | undefined => {
    if (viewType && campaignUuid && state?.viewType && state?.viewType[viewType]) {
        return state.viewType[viewType][campaignUuid] as T
    }
    return undefined
}

export const selectFilterModel = createSelector(
    [selectSelectedRegistrationView, selectSelectedCampaignUuid, selectReview],
    (viewType, campaignUuid, review) => {
        return (getViewTypeCampaignOptions<GridFilterModel>(review.filterModel, viewType, campaignUuid) ??
            ({} as GridFilterModel)) as GridFilterModel
    },
)

export const selectFilterOptions = createSelector(
    [selectSelectedRegistrationView, selectSelectedCampaignUuid, selectReview],
    (viewType, campaignUuid, review) => {
        return (getViewTypeCampaignOptions<OptionsState[]>(review.filterOptions, viewType, campaignUuid) ??
            []) as Array<OptionsState>
    },
)

export const selectSortItem = createSelector(
    [selectSelectedRegistrationView, selectSelectedCampaignUuid, selectReview],
    (viewType, campaignUuid, review) => {
        return getViewTypeCampaignOptions<SortItem>(review.sortItem, viewType, campaignUuid)
    },
)

export const selectSelectedSort = createSelector([selectSortItem], (gridSortItem): string | null => {
    if (gridSortItem && gridSortItem.field) {
        return getSortQueryText(gridSortItem.field, gridSortItem.sort || 'desc')
    } else {
        return null
    }
})

export const selectParentChildLocationData = (state: State) => selectReview(state).parentChildLocationData

export const selectFilteringParams = createSelector([selectFilterModel], filterModel => {
    if (!filterModel) return {}

    const SUFFIX_BY_OPERATOR_MAP = {
        contains: 'contains',
        startsWith: 'startswith',
        endsWith: 'endswith',
        '=': 'eq',
        '!=': 'neq',
        '>': 'gt',
        '>=': 'gte',
        '<': 'lt',
        '<=': 'lte',
    }

    const { items } = filterModel as GridFilterModel
    if (!items) {
        return {}
    }
    return items.reduce((acc: { [key: string]: string | string[] | undefined }, curr) => {
        const columnField = curr.columnField
        if (!columnField || !curr.value) return acc

        const suffix = SUFFIX_BY_OPERATOR_MAP[curr.operatorValue as keyof typeof SUFFIX_BY_OPERATOR_MAP]

        const [field, blockUuid] = columnField.split('+')
        if (blockUuid) {
            if (field === BLOCK_TYPE.NUMBER_SUBMISSION) {
                const query = `${blockUuid}_${suffix}_${curr.value}`
                return {
                    ...acc,
                    questions: acc.questions ? `${acc.questions},${query}` : query,
                }
            } else if (field === BLOCK_TYPE.QUESTION) {
                const query = `${blockUuid}_${curr.value}`
                return {
                    ...acc,
                    questions: acc.questions ? `${acc.questions},${query}` : query,
                }
            }
            return acc
        }

        if (columnField === FIELDS_TYPE.AGE) {
            if (curr.operatorValue === '=') return { ...acc, age: curr.value }
            return {
                ...acc,
                [`${FIELDS_TYPE.AGE}__${suffix}`]: curr.value,
            }
        } else if (columnField === FIELDS_TYPE.GENDER || columnField === FIELDS_TYPE.EMPLOYMENT_STATUS) {
            return {
                ...acc,
                [columnField]: acc[columnField] ? `${acc[columnField]},${curr.value}` : curr.value,
            }
        } else if (columnField === FIELDS_TYPE.MEET_REQUIREMENT) {
            return {
                ...acc,
                meet_requirement: acc.meet_requirement ? `${acc.meet_requirement},${curr.value}` : curr.value,
            }
        } else if (
            columnField === FIELDS_TYPE.IG_FOLLOWERS ||
            columnField === FIELDS_TYPE.FB_FRIENDS ||
            columnField === FIELDS_TYPE.TIKTOK_FOLLOWERS ||
            columnField === FIELDS_TYPE.TWITTER_FOLLOWERS ||
            columnField === FIELDS_TYPE.XIAOHONGSHU_FOLLOWERS
        ) {
            return {
                ...acc,
                social_media_follower_number_tier: acc.social_media_follower_number_tier
                    ? `${acc.social_media_follower_number_tier},${curr.value}`
                    : curr.value,
            }
        } else if (columnField === FIELDS_TYPE.NAME) {
            if (!suffix) return { ...acc, name: curr.value }
            return {
                ...acc,
                [`${FIELDS_TYPE.NAME}__${suffix}`]: curr.value,
            }
        } else if (columnField === FIELDS_TYPE.LOCATION) {
            const location: { rank1: string; rank2?: string } = JSON.parse(curr.value)
            const searchingId = location.rank2 ? location.rank2 : location.rank1
            return {
                ...acc,
                location: acc.location ? `${acc.location},${searchingId}` : searchingId,
            }
        } else if (columnField === FIELDS_TYPE.STATUS) {
            return {
                ...acc,
                is_viewed: acc.is_viewed ? `${acc.is_viewed},${curr.value}` : curr.value,
            }
        } else if (columnField === FIELDS_TYPE.HIGHLIGHT) {
            return {
                ...acc,
                highlighted: acc.highlighted ? `${acc.highlighted},${curr.value}` : curr.value,
            }
        } else if (columnField === FIELDS_TYPE.PARTIPOSTER_TAGS) {
            return {
                ...acc,
                tags: acc.tags ? `${acc.tags},${curr.value}` : curr.value,
            }
        }
        return acc
    }, {})
})

export const selectSelectedPriorityListUuids = (state: State): string[] => selectReview(state).selectedPriorityListUuids

export const selectSelectedMuiTableRef = (state: State): React.MutableRefObject<GridApi> | null =>
    selectReview(state).selectedMuiTableRef

export const selectSelectedRowIds = (state: State): string[] => selectReview(state).selectedRowIds

export const selectEditRequestHistoryModal = (state: State): EditRequestHistoryModalState =>
    selectReview(state).editRequestHistoryModal

export const selectShowReorderOverlay = (state: State): boolean => selectReview(state).showReorderOverlay

export const selectSearchablePlatforms = createSelector(
    [selectTasksPlatformTypes, selectSidebarTask, selectSelectedSubmissionView, selectSelectedRegistrationView],
    (platforms, sidebarTask, selectedSubmissionView, selectedRegistrationView) => {
        const searchablePlatforms = platforms.filter(platform => !unSearchablePlatforms.includes(platform))
        const allowedRegistrationStatus = Object.values(REGISTRATION_VIEW_ENUM)
        const allowedSubmissionView = [SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS]

        const isRegistrationViewIncluded =
            selectedRegistrationView && allowedRegistrationStatus.includes(selectedRegistrationView)
        const isSubmissionViewIncluded =
            selectedSubmissionView && allowedSubmissionView.includes(selectedSubmissionView)

        if (
            isSubmissionViewIncluded ||
            isRegistrationViewIncluded ||
            !sidebarTask ||
            unSearchablePlatforms.includes(sidebarTask.platform)
        ) {
            // maintain the existing default platform (IG)
            // only batch submission will have default platform ig, since it is needed default IG in card view (batch submission)
            let defaultPlatform: PLATFORM_ENUM[] = [PLATFORM_ENUM.IG]

            // change default platform if not in submission view (reject/dropouts excluded)
            // table view will have default empty, since it required to show correct columns
            if (isRegistrationViewIncluded) {
                defaultPlatform = []
            }
            return searchablePlatforms.length > 0 ? searchablePlatforms : defaultPlatform
        }
        return searchablePlatforms.filter(platform => platform === sidebarTask.platform)
    },
)

export const selectFollowerFilterable = (platform: PLATFORM_ENUM, campaign: CampaignState) =>
    createSelector(
        [selectFollowerTierFilterOptions(platform, campaign), selectFilteringParams],
        (followersTiresOptions, filteringParams) => {
            const followerTier = followersTiresOptions.map(option => option.value.toString())
            return filteringParams?.social_media_follower_number_tier &&
                !followerTier.includes(filteringParams.social_media_follower_number_tier as string)
                ? false
                : true
        },
    )

export const selectIsInternalViewOnlyStepHelper = createSelector(
    (state: State) => selectIsEngagementStepTypeHelper(state),
    isEngagementStepType => {
        return (stepType: STEP_TYPE_ENUM): boolean => {
            // in all submission view, we needs to hide proof step and engagement step content
            // if submission in pending/edit request status
            return (
                isEngagementStepType(stepType) ||
                [
                    STEP_TYPE_ENUM.SUBMIT_PROOF_IG_SCREENSHOT,
                    STEP_TYPE_ENUM.POST_AND_SUBMIT_PROOF_INSTAGRAM_POST_URL,
                    STEP_TYPE_ENUM.SUBMIT_PROOF_IG_URL,
                    STEP_TYPE_ENUM.SUBMIT_PROOF_FB_URL,
                    STEP_TYPE_ENUM.POST_AND_SUBMIT_PROOF_FACEBOOK_POST_URL,
                    STEP_TYPE_ENUM.POST_AND_SUBMIT_PROOF_TIKTOK_POST_URL,
                    STEP_TYPE_ENUM.POST_AND_SUBMIT_PROOF_INSTAGRAM_REEL_URL,
                    STEP_TYPE_ENUM.POST_AND_SUBMIT_PROOF_XIAOHONGSHU_POST_URL,
                ].includes(stepType)
            )
        }
    },
)

export const selectDiscussionModal = (state: State): DiscussionModalState => selectReview(state).discussionModal
export const selectDiscussionModalIsOpen = (state: State): boolean => selectReview(state).discussionModal.isOpen
export const selectOriginalCaptionModal = (state: State): OriginalCaptionModalState =>
    selectReview(state).originalCaptionModal
export const selectOriginalCaptionModalIsOpen = (state: State): boolean =>
    selectReview(state).originalCaptionModal.isOpen
export const selectCaptionDetailModal = (state: State): CaptionDetailModalState =>
    selectReview(state).captionDetailModal
export const selectCaptionDetailModalIsOpen = (state: State): boolean => selectReview(state).captionDetailModal.isOpen
export const selectRejectAllSubmissionModal = (state: State) => selectReview(state).rejectAllSubmissionModal
export const selectSingleTaskViews = (state: State): SUBMISSION_VIEW_ENUM[] => selectReview(state).singleTaskViews

export type timelineInfoProps = {
    title: string
    reminderMessage: string
    campaignStartDate: string
    campaignEndDate: string
    reviewStartDate: string
    reviewEndDate: string
    reviewDayLeft: number
    requestEditEndDate: string
    requestEditDayLeft: number
}

const getProgressByDate = (startDate: string, endDate: string, country: Country, totalPercent = 100) => {
    const totalDays = getDurationByDaysWithDates(endDate, startDate)
    const pastedDays = -getDurationByDays(startDate, country)
    const progress = Math.ceil((pastedDays / totalDays) * totalPercent)
    return totalDays > 0 ? (progress <= 0 ? 0 : progress >= totalPercent ? totalPercent : progress) : 0
}

/*
    for all submission progress calculation, note that since all submission doesn't have fixed review end date
    we display 97% when current date is over latest step end date + 7 days
    that's can let user know he still has time to approve submisions.
*/
const getNoFixEndDateProgressByDate = (startDate: string, endDate: string, country: Country) => {
    const trueEndDate = addDay(endDate, 7)
    const totalDays = getDurationByDaysWithDates(trueEndDate, startDate)
    const pastedDays = -getDurationByDays(startDate, country)
    const progress = Math.ceil((pastedDays / totalDays) * 97)
    return totalDays > 0 ? (progress <= 0 ? 0 : progress >= 97 ? 97 : progress) : 0
}

/*
    for single submission progress calculation, since single step submission has 3 dots.
    the middle orange dot(50%) represent step deadline. so if current date is reach to step deadline.
    the progres will equal to 50% for the red dot progress display, the logic is like below:
    a. if step start date is earlier then step end date - 7 days:
        i.      0%: campaign launched/before current step start
        ii.     calculation starts (0%~40%):        first step starts, step deadline before 7 days
        iii.    calculation starts (40%~47%):       step deadline before 7 day to step deadline before 1 day
        iv.     calculation starts (47%~50%):       step deadline before one day to step deadline over
        v.      reach orange dot (50%): reach       step deadline
        vi.     if there’s next step deadline:      calculation ends (50~100%): reach step deadline to reach next step deadline
        vii.    if there’s NO next step deadline:   calculation ends (50~97%): reach step deadline to last step date + 7 days (because there’s no fix end date, so we give 7 days buffer time)
    b. if step start date is later then step end date - 7 days (skip step deadline before 7 day check):
        i.      0%: campaign launched/before current step start
        ii.     calculation starts (0% ~47%): first step starts, step deadline before one day
        iii.    calculation starts (47% ~50%): step deadline before one day to step deadline over
        iv.     reach orange dot (50%): reach step deadline
        v.      if there’s next step deadline:      calculation ends (50~100%): reach step deadline to reach next step deadline
        vi.     if there’s NO next step deadline:   calculation ends (50~97%): reach step deadline to last step date + 7 days (because there’s no fix end date, so we give 7 days buffer time)
*/
const getSubmissionProgressByDate = (
    stepStartDate: string,
    stepEndDate: string,
    country: Country,
    nextStepEndDate?: string,
) => {
    let progress

    const requestEditDeadlineBeforeOneWeek = addDay(stepEndDate, -7)
    const requestEditDeadlineBeforeOneDay = addDay(stepEndDate, -1)

    if (getDurationByDaysWithDates(stepStartDate, requestEditDeadlineBeforeOneWeek) > 0) {
        if (!dateIsInPast(requestEditDeadlineBeforeOneWeek, country)) {
            // a. ii. calculation starts (0%~40%): first step starts, step deadline before 7 days
            progress = getProgressByDate(stepStartDate, requestEditDeadlineBeforeOneWeek, country, 40)
        } else if (
            dateIsInPast(requestEditDeadlineBeforeOneWeek, country) &&
            !dateIsInPast(requestEditDeadlineBeforeOneDay, country)
        ) {
            // a. iii. calculation starts (40%~47%): step deadline before 7 day to step deadline before 1 day
            progress =
                40 + getProgressByDate(requestEditDeadlineBeforeOneWeek, requestEditDeadlineBeforeOneDay, country, 7)
        } else if (dateIsInPast(requestEditDeadlineBeforeOneDay, country) && !dateIsInPast(stepEndDate, country)) {
            // a. iv. calculation starts (47%~50%): step deadline before one day to step deadline over
            progress = 47 + getProgressByDate(requestEditDeadlineBeforeOneDay, stepEndDate, country, 3)
        } else {
            // a.v. reach orange dot (50%): reach step deadline
            if (nextStepEndDate) {
                // a.vi. if there’s next step deadline:
                // calculation ends (50~100%): reach step deadline to reach next step deadline
                progress =
                    50 +
                    (stepEndDate === nextStepEndDate
                        ? 50
                        : getProgressByDate(stepEndDate, nextStepEndDate, country, 50))
            } else {
                // a. vii. if there’s NO next step deadline:
                // calculation ends (50~97%): reach step deadline to last step date + 7 days (because there’s no fix end date, so we give 7 days buffer time)
                const stepEndDateAfterSevenDays = addDay(stepEndDate, 7)
                progress = 50 + getProgressByDate(stepEndDate, stepEndDateAfterSevenDays, country, 47)
            }
        }
    } else {
        //  step end date less then 7 days, ignore requestEditDeadlineBeforeOneWeek check
        //  only display less then one day case
        if (!dateIsInPast(requestEditDeadlineBeforeOneDay, country)) {
            // b. ii. calculation starts (0% ~47%): first step starts, step deadline before one day
            progress = getProgressByDate(stepStartDate, requestEditDeadlineBeforeOneWeek, country, 47)
        } else if (dateIsInPast(requestEditDeadlineBeforeOneDay, country) && !dateIsInPast(stepEndDate, country)) {
            // b. iii. calculation starts (47% ~50%): step deadline before one day to step deadline over
            progress = 47 + getProgressByDate(requestEditDeadlineBeforeOneDay, stepEndDate, country, 3)
        } else {
            // step deadline is over
            // b. iv. calculation ends (100%): reach next step deadline
            if (nextStepEndDate) {
                // b. v. if there’s next step deadline:
                // calculation ends (50~100%): reach step deadline to reach next step deadline
                progress =
                    50 +
                    (stepEndDate === nextStepEndDate
                        ? 50
                        : getProgressByDate(stepEndDate, nextStepEndDate, country, 50))
            } else {
                // b. vi. if there’s NO next step deadline:
                // calculation ends (50~97%): reach step deadline to last step date + 7 days (because there’s no fix end date, so we give 7 days buffer time)
                const stepEndDateAfterSevenDays = addDay(stepEndDate, 7)
                progress = 50 + getProgressByDate(stepEndDate, stepEndDateAfterSevenDays, country, 47)
            }
        }
    }
    return progress
}

interface ReminderInfoProps {
    reminderType?: 'warning' | 'error'
    reminderKeys: string[]
}

const getSubmissionReminderByDate = (
    t: TFunction,
    stepEndDate: string,
    country: Country,
    nextStepEndDate?: string,
): ReminderInfoProps => {
    let reminderKeys: string[] = []
    let reminderType: 'warning' | 'error' | undefined

    const requestEditDeadlineBeforeOneWeek = addDay(stepEndDate, -7)
    const requestEditDeadlineBeforeOneDay = addDay(stepEndDate, -1)

    if (nextStepEndDate) {
        if (
            dateIsInPast(requestEditDeadlineBeforeOneWeek, country) &&
            !dateIsInPast(requestEditDeadlineBeforeOneDay, country)
        ) {
            reminderKeys = [
                t('warningMessage.businessLogic.lessThanOneWeekToRequestEdit'),
                t('warningMessage.businessLogic.approvePartipostersBy', {
                    date: parseBackendDate(nextStepEndDate, country, { addSeconds: -1 }),
                }),
            ]
            reminderType = 'warning'
        } else if (dateIsInPast(requestEditDeadlineBeforeOneDay, country) && !dateIsInPast(stepEndDate, country)) {
            reminderKeys = [
                t('warningMessage.businessLogic.lessThanOneDayToRequestEdit'),
                t('warningMessage.businessLogic.approvePartipostersBy', {
                    date: parseBackendDate(nextStepEndDate, country, { addSeconds: -1 }),
                }),
            ]
            reminderType = 'warning'
        } else if (dateIsInPast(stepEndDate, country) && !dateIsInPast(nextStepEndDate, country)) {
            // step deadline is over
            reminderKeys = [
                t('warningMessage.businessLogic.canNoLongerRequestEdit'),
                t('warningMessage.businessLogic.approvePartipostersBy', {
                    date: parseBackendDate(nextStepEndDate, country, { addSeconds: -1 }),
                }),
            ]
            reminderType = 'warning'
        } else if (dateIsInPast(nextStepEndDate, country)) {
            reminderKeys = [t('warningMessage.businessLogic.canNoLongerApproveAndRequestEdit')]
            reminderType = 'error'
        }
    } else {
        if (
            dateIsInPast(requestEditDeadlineBeforeOneWeek, country) &&
            !dateIsInPast(requestEditDeadlineBeforeOneDay, country)
        ) {
            reminderKeys = [
                t('warningMessage.businessLogic.lessThanOneWeekToRequestEdit'),
                t('warningMessage.businessLogic.canStillApprovePartiposters'),
            ]
            reminderType = 'warning'
        } else if (dateIsInPast(requestEditDeadlineBeforeOneDay, country) && !dateIsInPast(stepEndDate, country)) {
            reminderKeys = [
                t('warningMessage.businessLogic.lessThanOneDayToRequestEdit'),
                t('warningMessage.businessLogic.canStillApprovePartiposters'),
            ]
            reminderType = 'warning'
        } else if (dateIsInPast(stepEndDate, country)) {
            // step deadline is over
            reminderKeys = [
                t('warningMessage.businessLogic.canNoLongerRequestEdit'),
                t('warningMessage.businessLogic.canStillApprovePartiposters'),
            ]
            reminderType = 'warning'
        }
    }

    return { reminderKeys, reminderType }
}

export type ContentType = 'registration' | 'allSubmission' | 'submissionNotInLastStep' | 'submissionInLastStep'

interface SubmitDate {
    startDate: string
    endDate: string
}

interface RequestEditDate {
    endDate?: string
    dayLeft?: number
}

interface RegistrationReviewDate {
    reviewStartDate?: string
    reviewEndDate?: string
    submitEndDate?: string
    reviewDayLeft?: number
}
interface SubmissionReviewDate {
    startDate?: string
    endDate?: string
    dayLeft?: number
}

interface SelectTimelineInfoProps {
    submissionView: SUBMISSION_VIEW_ENUM | null
    registrationView: REGISTRATION_VIEW_ENUM | null
    overviewView: OVERVIEW_ENUM | null
    contentType?: ContentType
    reminderType?: 'warning' | 'error'
    reminderMessageKey?: string[]
    submitDate: SubmitDate
    campaignOverallStepProgress?: number
    requestEditDate?: RequestEditDate
    registrationDate?: RegistrationReviewDate
    registrationProgress?: number
    submissionReviewDate?: SubmissionReviewDate
    submissionProgress?: number
}

export const selectTimelineInfo = (t: TFunction) =>
    createSelector(
        [
            selectSelectedSubmissionView,
            selectSelectedRegistrationView,
            selectSelectedOverview,
            selectSidebarStep,
            selectRegistrationForm,
            selectSelectedCampaignUuid,
            selectSummaryById,
            makeNextStepSelector,
            selectSidebarTask,
        ],
        (
            submissionView,
            registrationView,
            overviewView,
            selectedStep,
            registrationForm,
            selectedCampaignUuid,
            campaignSummaryById,
            selectNextStep,
            sidebarTask,
        ): SelectTimelineInfoProps | null => {
            if (!selectedCampaignUuid || !selectedStep) {
                return null
            }
            const campaignSummary = campaignSummaryById[selectedCampaignUuid]
            if (!campaignSummary) {
                return null
            }
            const selectedNextStep = selectNextStep(_, { step: selectedStep, task: sidebarTask })

            let contentType: ContentType | undefined,
                reminderInfo: ReminderInfoProps | undefined,
                registrationReviewStartDate,
                registrationReviewEndDate,
                registrationSubmitEndDate,
                registrationReviewDayLeft,
                registrationProgress,
                submissionProgress,
                requestEditEndDate,
                requestEditDayLeft,
                submissionReviewStartDate,
                submissionReviewEndDate,
                submissionReviewDayLeft,
                submitStartDate,
                submitEndDate,
                campaignOverallStepProgress

            if (submissionView) {
                if (submissionView === SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS) {
                    contentType = 'allSubmission'
                    // needs to use ampaignSummary.time_window.start since campaignSummary.overall_step_time_window.start includes registration start date
                    submitStartDate = parseBackendDate(campaignSummary.time_window.start, campaignSummary.country)
                    submitEndDate = parseBackendDate(
                        campaignSummary.overall_step_time_window.end,
                        campaignSummary.country,
                        {
                            showTime: false,
                            showYear: true,
                            addSeconds: -1,
                        },
                    )

                    campaignOverallStepProgress = getNoFixEndDateProgressByDate(
                        campaignSummary.time_window.start,
                        campaignSummary.overall_step_time_window.end,
                        campaignSummary.country,
                    )
                } else {
                    submitStartDate = parseBackendDate(selectedStep.time_window.start, campaignSummary.country)
                    submitEndDate = parseBackendDate(selectedStep.time_window.end, campaignSummary.country, {
                        showTime: false,
                        showYear: true,
                        addSeconds: -1,
                    })
                    requestEditEndDate = parseBackendDate(selectedStep.time_window.end, campaignSummary.country, {
                        showTime: false,
                        showYear: true,
                        addSeconds: -1,
                    })
                    requestEditDayLeft = getDurationByDays(selectedStep.time_window.end, campaignSummary.country)
                    submissionReviewStartDate = parseBackendDate(
                        selectedStep.time_window.start,
                        campaignSummary.country,
                    )
                    if (selectedNextStep) {
                        contentType = 'submissionNotInLastStep'
                        submissionProgress = getSubmissionProgressByDate(
                            selectedStep.time_window.start,
                            selectedStep.time_window.end,
                            campaignSummary.country,
                            selectedNextStep.time_window.end,
                        )
                        reminderInfo = getSubmissionReminderByDate(
                            t,
                            selectedStep.time_window.end,
                            campaignSummary.country,
                            selectedNextStep.time_window.end,
                        )
                        submissionReviewEndDate = parseBackendDate(
                            selectedNextStep.time_window.end,
                            campaignSummary.country,
                            {
                                showTime: false,
                                showYear: true,
                                addSeconds: -1,
                            },
                        )
                        submissionReviewDayLeft = getDurationByDays(
                            selectedNextStep.time_window.end,
                            campaignSummary.country,
                        )
                    } else {
                        contentType = 'submissionInLastStep'
                        if (campaignSummary.status === CAMPAIGN_STATUS_ENUM.CLOSED) {
                            submissionProgress = 100
                        } else {
                            submissionProgress = getSubmissionProgressByDate(
                                selectedStep.time_window.start,
                                selectedStep.time_window.end,
                                campaignSummary.country,
                            )
                        }

                        reminderInfo = getSubmissionReminderByDate(
                            t,
                            selectedStep.time_window.end,
                            campaignSummary.country,
                        )
                    }
                }
            } else if (registrationView) {
                if (!registrationForm) return null
                submitStartDate = parseBackendDate(registrationForm.time_window.start, campaignSummary.country)
                submitEndDate = parseBackendDate(registrationForm.time_window.end, campaignSummary.country, {
                    showTime: false,
                    showYear: true,
                    addSeconds: -1,
                })
                contentType = 'registration'
                registrationProgress = getProgressByDate(
                    registrationForm.time_window.start,
                    campaignSummary.time_window.end,
                    campaignSummary.country,
                )
                if (dateIsInPast(campaignSummary.time_window.end, campaignSummary.country)) {
                    reminderInfo = {
                        reminderKeys: [t('errorMessage.businessLogic.cantApprovePartipostersTitle')],
                        reminderType: 'error',
                    }
                }
                registrationReviewStartDate = parseBackendDate(
                    registrationForm.time_window.start,
                    campaignSummary.country,
                )
                // use earliest step end time: campaignSummary.time_window.end
                registrationReviewEndDate = parseBackendDate(campaignSummary.time_window.end, campaignSummary.country, {
                    showTime: false,
                    showYear: true,
                    addSeconds: -1,
                })
                registrationReviewDayLeft = getDurationByDays(campaignSummary.time_window.end, campaignSummary.country)
            } else {
                //overview page, submitDate = all submission submit date, needs to assign registratetion end date here for support registration hover menu's submit date display
                submitStartDate = parseBackendDate(campaignSummary.time_window.start, campaignSummary.country)
                submitEndDate = parseBackendDate(
                    campaignSummary.overall_step_time_window.end,
                    campaignSummary.country,
                    {
                        showTime: false,
                        showYear: true,
                        addSeconds: -1,
                    },
                )

                campaignOverallStepProgress = getNoFixEndDateProgressByDate(
                    campaignSummary.time_window.start,
                    campaignSummary.overall_step_time_window.end,
                    campaignSummary.country,
                )
                if (registrationForm) {
                    registrationProgress = getProgressByDate(
                        registrationForm.time_window.start,
                        campaignSummary.time_window.end,
                        campaignSummary.country,
                    )
                    registrationReviewStartDate = parseBackendDate(
                        registrationForm.time_window.start,
                        campaignSummary.country,
                    )
                    registrationSubmitEndDate = parseBackendDate(
                        registrationForm.time_window.end,
                        campaignSummary.country,
                        {
                            showTime: false,
                            showYear: true,
                            addSeconds: -1,
                        },
                    )
                    // use earliest step end time: campaignSummary.time_window.end
                    registrationReviewEndDate = parseBackendDate(
                        campaignSummary.time_window.end,
                        campaignSummary.country,
                        {
                            showTime: false,
                            showYear: true,
                            addSeconds: -1,
                        },
                    )
                    registrationReviewDayLeft = getDurationByDays(
                        campaignSummary.time_window.end,
                        campaignSummary.country,
                    )
                }
            }

            return {
                submissionView,
                registrationView,
                overviewView,
                contentType: contentType,
                reminderType: reminderInfo?.reminderType,
                reminderMessageKey: reminderInfo?.reminderKeys,
                submitDate: {
                    startDate: submitStartDate,
                    endDate: submitEndDate,
                },
                campaignOverallStepProgress: campaignOverallStepProgress,
                requestEditDate: {
                    endDate: requestEditEndDate,
                    dayLeft: requestEditDayLeft,
                },
                registrationDate: {
                    reviewStartDate: registrationReviewStartDate,
                    reviewEndDate: registrationReviewEndDate,
                    submitEndDate: registrationSubmitEndDate,
                    reviewDayLeft: registrationReviewDayLeft,
                },
                registrationProgress: registrationProgress,
                submissionReviewDate: {
                    startDate: submissionReviewStartDate,
                    endDate: submissionReviewEndDate,
                    dayLeft: submissionReviewDayLeft,
                },
                submissionProgress: submissionProgress,
            }
        },
    )

// selector to show review warning based on team type & user type
export const selectShowReviewWarningSaas = (campaignUuid: string) =>
    createSelector([selectIsInternalUser, selectSummaryById], (isInternalUser, campaignSummaries) => {
        const teamType = campaignSummaries[campaignUuid]?.brand?.team?.type
        if (teamType === 'saas' && !isInternalUser) {
            return true
        }

        return false
    })
